/**
 * Zoketo OTP Login System
 * This script handles OTP-based authentication, Google SSO, and Facebook SSO integration
 * for Shopify stores. It provides a modern, secure login experience with multiple
 * authentication methods.
 */

console.log("🚀 login-override.js - Script starting to load...");

// Helper: run only on /account/login (supports locale prefixes like /ar/account/login)
function isOnAccountLoginPage() {
  try {
    const path = window.location && window.location.pathname ? window.location.pathname : '';
    // Normalize trailing slash
    return /\/account\/login\/?$/.test(path);
  } catch (e) {
    return false;
  }
}

/**
 * Detects if the page is running inside a WebView (MobiLoud, native app wrapper, etc.)
 * Returns true if inside a WebView, false if in a normal browser.
 * Used to switch from popup-based SSO to same-window redirect SSO.
 */
function isInWebView() {
  var ua = navigator.userAgent || '';

  // Android WebView: standard 'wv' flag injected by Android WebViewClient
  if (/; wv\)/.test(ua)) return true;
  // Android WebView without Chrome (but with Version/ tag)
  if (/Android/.test(ua) && /Version\/\d/.test(ua) && !/Chrome\/\d/.test(ua)) return true;
  // Android: no window.chrome but has Chrome UA (common in React Native / Capacitor WebViews)
  if (/Android/.test(ua) && /Chrome\/\d/.test(ua) && typeof window.chrome === 'undefined') return true;

  // iOS WebView: has AppleWebKit but NOT Safari token
  if (/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua)) return true;

  // MobiLoud / MobiCloud specific detection
  if (/MobiLoud/i.test(ua)) return true;
  if (/MobiCloud/i.test(ua)) return true;
  if (typeof window.MobiLoud !== 'undefined') return true;
  if (typeof window.MobiCloud !== 'undefined') return true;

  // Standalone PWA mode
  if (window.navigator.standalone === true) return true;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;

  return false;
}

// Use existing Hurme font from theme
(function applyHurmeFont() {
  console.log("📝 Applying existing Hurme font from theme...");
  if (document.getElementById('hyperotp-hurme-font-style')) return;

  // Create CSS to use the existing Hurme font from theme
  const style = document.createElement('style');
  style.id = 'hyperotp-hurme-font-style';
  style.innerHTML = `
    /* Use existing Hurme font from theme */
    :root {
      --font-hurme: 'Hurme', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
    }
    
    /* Arabic language support */
    [lang="ar"], [lang="ar-SA"], [lang="ar-AE"], [lang="ar-KW"], [lang="ar-QA"], [lang="ar-BH"], [lang="ar-OM"] {
      direction: rtl;
      text-align: right;
    }
    
    /* Force phone numbers to display LTR even in Arabic */
    [lang="ar"] .otp-phone-number,
    [lang="ar-SA"] .otp-phone-number,
    [lang="ar-AE"] .otp-phone-number,
    [lang="ar-KW"] .otp-phone-number,
    [lang="ar-QA"] .otp-phone-number,
    [lang="ar-BH"] .otp-phone-number,
    [lang="ar-OM"] .otp-phone-number {
      direction: ltr !important;
      text-align: left !important;
      unicode-bidi: bidi-override !important;
    }
    
    /* Arabic button arrow positioning and direction */
    [lang="ar"] .otp-btn,
    [lang="ar-SA"] .otp-btn,
    [lang="ar-AE"] .otp-btn,
    [lang="ar-KW"] .otp-btn,
    [lang="ar-QA"] .otp-btn,
    [lang="ar-BH"] .otp-btn,
    [lang="ar-OM"] .otp-btn,
    [lang="ar"] .otp-verify-btn,
    [lang="ar-SA"] .otp-verify-btn,
    [lang="ar-AE"] .otp-verify-btn,
    [lang="ar-KW"] .otp-verify-btn,
    [lang="ar-QA"] .otp-verify-btn,
    [lang="ar-BH"] .otp-verify-btn,
    [lang="ar-OM"] .otp-verify-btn,
    [lang="ar"] .signup-btn,
    [lang="ar-SA"] .signup-btn,
    [lang="ar-AE"] .signup-btn,
    [lang="ar-KW"] .signup-btn,
    [lang="ar-QA"] .signup-btn,
    [lang="ar-BH"] .signup-btn,
    [lang="ar-OM"] .signup-btn {
      flex-direction: row-reverse !important;
    }
    
    /* Flip arrow icon direction for Arabic (point left instead of right) */
    [lang="ar"] .otp-btn-arrow svg,
    [lang="ar-SA"] .otp-btn-arrow svg,
    [lang="ar-AE"] .otp-btn-arrow svg,
    [lang="ar-KW"] .otp-btn-arrow svg,
    [lang="ar-QA"] .otp-btn-arrow svg,
    [lang="ar-BH"] .otp-btn-arrow svg,
    [lang="ar-OM"] .otp-btn-arrow svg,
    [lang="ar"] .otp-verify-btn-arrow svg,
    [lang="ar-SA"] .otp-verify-btn-arrow svg,
    [lang="ar-AE"] .otp-verify-btn-arrow svg,
    [lang="ar-KW"] .otp-verify-btn-arrow svg,
    [lang="ar-QA"] .otp-verify-btn-arrow svg,
    [lang="ar-BH"] .otp-verify-btn-arrow svg,
    [lang="ar-OM"] .otp-verify-btn-arrow svg,
    [lang="ar"] .signup-btn-arrow svg,
    [lang="ar-SA"] .signup-btn-arrow svg,
    [lang="ar-AE"] .signup-btn-arrow svg,
    [lang="ar-KW"] .signup-btn-arrow svg,
    [lang="ar-QA"] .signup-btn-arrow svg,
    [lang="ar-BH"] .signup-btn-arrow svg,
    [lang="ar-OM"] .signup-btn-arrow svg {
      transform: scaleX(-1) !important;
    }
    
    [lang="ar"] #otp-login-container,
    [lang="ar-SA"] #otp-login-container,
    [lang="ar-AE"] #otp-login-container,
    [lang="ar-KW"] #otp-login-container,
    [lang="ar-QA"] #otp-login-container,
    [lang="ar-BH"] #otp-login-container,
    [lang="ar-OM"] #otp-login-container {
      direction: rtl;
      text-align: right;
    }

    [lang="ar"] .country-code-wrapper,
    [lang="ar-SA"] .country-code-wrapper,
    [lang="ar-AE"] .country-code-wrapper,
    [lang="ar-KW"] .country-code-wrapper,
    [lang="ar-QA"] .country-code-wrapper,
    [lang="ar-BH"] .country-code-wrapper,
    [lang="ar-OM"] .country-code-wrapper {
      direction: ltr !important;
      border-right: none !important;
      border-left: 0px solid #D9D9D9 !important;
      margin-left: 10px !important;
      margin-right: -6px !important;
      padding: 0 22px 0 10px !important;
      border-radius: 0 8px 8px 0;
    }
    
    [lang="ar"] .input-group input,
    [lang="ar-SA"] .input-group input,
    [lang="ar-AE"] .input-group input,
    [lang="ar-KW"] .input-group input,
    [lang="ar-QA"] .input-group input,
    [lang="ar-BH"] .input-group input,
    [lang="ar-OM"] .input-group input {
      direction: rtl;
      text-align: right;
    }
    
    [lang="ar"] .input-group input:not(:placeholder-shown),
    [lang="ar-SA"] .input-group input:not(:placeholder-shown),
    [lang="ar-AE"] .input-group input:not(:placeholder-shown),
    [lang="ar-KW"] .input-group input:not(:placeholder-shown),
    [lang="ar-QA"] .input-group input:not(:placeholder-shown),
    [lang="ar-BH"] .input-group input:not(:placeholder-shown),
    [lang="ar-OM"] .input-group input:not(:placeholder-shown) {
      direction: ltr;
      text-align: right !important;
    }
    
    [lang="ar"] .otp-login-title,
    [lang="ar-SA"] .otp-login-title,
    [lang="ar-AE"] .otp-login-title,
    [lang="ar-KW"] .otp-login-title,
    [lang="ar-QA"] .otp-login-title,
    [lang="ar-BH"] .otp-login-title,
    [lang="ar-OM"] .otp-login-title,
    [lang="ar"] .otp-verify-title,
    [lang="ar-SA"] .otp-verify-title,
    [lang="ar-AE"] .otp-verify-title,
    [lang="ar-KW"] .otp-verify-title,
    [lang="ar-QA"] .otp-verify-title,
    [lang="ar-BH"] .otp-verify-title,
    [lang="ar-OM"] .otp-verify-title,
    [lang="ar"] .signup-title,
    [lang="ar-SA"] .signup-title,
    [lang="ar-AE"] .signup-title,
    [lang="ar-KW"] .signup-title,
    [lang="ar-QA"] .signup-title,
    [lang="ar-BH"] .signup-title,
    [lang="ar-OM"] .signup-title {
      text-align: center;
    }
    
    [lang="ar"] .login-subtitle,
    [lang="ar-SA"] .login-subtitle,
    [lang="ar-AE"] .login-subtitle,
    [lang="ar-KW"] .login-subtitle,
    [lang="ar-QA"] .login-subtitle,
    [lang="ar-BH"] .login-subtitle,
    [lang="ar-OM"] .login-subtitle,
    [lang="ar"] .otp-verify-subtitle,
    [lang="ar-SA"] .otp-verify-subtitle,
    [lang="ar-AE"] .otp-verify-subtitle,
    [lang="ar-KW"] .otp-verify-subtitle,
    [lang="ar-QA"] .otp-verify-subtitle,
    [lang="ar-BH"] .otp-verify-subtitle,
    [lang="ar-OM"] .otp-verify-subtitle,
    [lang="ar"] .signup-subtitle,
    [lang="ar-SA"] .signup-subtitle,
    [lang="ar-AE"] .signup-subtitle,
    [lang="ar-KW"] .signup-subtitle,
    [lang="ar-QA"] .signup-subtitle,
    [lang="ar-BH"] .signup-subtitle,
    [lang="ar-OM"] .signup-subtitle {
      text-align: center;
    }
    
    /* Apply Hurme font to all login form elements */
    #otp-login-container,
    #otp-login-container *,
    .otp-login-title,
    .login-subtitle,
    .otp-verify-title,
    .otp-verify-subtitle,
    .otp-phone-number,
    .otp-change-link,
    .otp-resend-text,
    .otp-resend-link,
    .divider-text,
    .social-btn-text,
    .signup-title,
    .signup-subtitle,
    .signup-checkbox-label,
    input,
    button,
    select,
    .otp-btn,
    .otp-verify-btn,
    .signup-btn,
    .social-btn,
    .email-btn {
      font-family: var(--font-hurme) !important;
    }
    
    /* Additional selectors to catch theme-specific font classes */
    .otp-login-title,
    .otp-verify-title,
    .signup-title {
      font-family: var(--font-hurme) !important;
      font-weight: 700 !important;
    }
    
    .login-subtitle,
    .otp-verify-subtitle,
    .signup-subtitle {
      font-family: var(--font-hurme) !important;
      font-weight: 300 !important;
    }
    
    .otp-btn,
    .otp-verify-btn,
    .signup-btn {
      font-family: var(--font-hurme) !important;
      font-weight: 400 !important
      text-transform: none !important;
    }
    
    .social-btn,
    .email-btn {
      font-family: var(--font-hurme) !important;
      font-weight: 400 !important;
    }
  `;
  document.head.appendChild(style);

  console.log("✅ Hurme font from theme applied to login form");
})();

// Add a temporary CSS at the very top to immediately hide the default Shopify login form
(function injectTempHideLoginCSS() {
  console.log("🎨 Injecting temporary hide login CSS...");
  if (!isOnAccountLoginPage()) return;
  if (document.getElementById('hyperotp-temp-hide-login-style')) return;
  const style = document.createElement("style");
  style.id = "hyperotp-temp-hide-login-style";
  style.innerHTML = `
    form[action="/account/login"],
    #customer_login,
    .customer.login,
    [data-login-form],
    .shopify-section--main-password,
    .shopify-section--main-login,
    .section-header__title,
    .login-title,
    .form-title,
    .customer-login-title,
    form[action="/account/login"] *,
    #customer_login h1,
    #customer_login h2,
    .customer.login h1,
    .customer.login h2,
    .login-form,
    .login-form *,
    .container .row .col-lg-6:has(.login-form),
    .container .row .col-lg-6 .login-form,
    .container .row .col-lg-6 h2,
    .container .row .col-lg-6 form,
    .container .row .col-lg-6 .form-group,
    .container .row .col-lg-6 .btn,
    #CustomerLoginForm,
    #CustomerLoginForm *,
    .account-form,
    .account-form *,
    h1.title.row {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      z-index: -1 !important;
    }
  `;
  document.head.appendChild(style);
  console.log("✅ Temporary hide login CSS injected");
})();

console.log("✅ login-override.js loaded");

/**
 * SECTION: Initial Setup
 * Injects required scripts and sets up the base configuration
 */

// Function to check which social logins are enabled
function getEnabledSocialLogins() {
  if (!window.hyperOTPSettings || !window.hyperOTPSettings.socialLogins || window.hyperOTPSettings.socialLogins.trim() === '') {
    return { google: false, facebook: false };
  }
  const socialLogins = window.hyperOTPSettings.socialLogins.toLowerCase().split(',');
  const result = {
    google: socialLogins.includes('google'),
    facebook: socialLogins.includes('facebook')
  };
  return result;
}

// Function to check if OTP form should be shown
function shouldShowOTPForm() {
  if (!window.hyperOTPSettings) return true;
  const method = window.hyperOTPSettings.loginMethod;
  return method === "ONLY_PHONE" || method === "EMAIL_AND_PHONE";
}

// Function to check if email password login should be shown
function shouldShowEmailPasswordLogin() {
  if (!window.hyperOTPSettings) return true;
  const method = window.hyperOTPSettings.loginMethod;
  return method === "ONLY_EMAIL_PASSWORD" || method === "EMAIL_AND_PHONE";
}

// Function to detect website language
function detectWebsiteLanguage() {
  // Check HTML lang attribute first
  const htmlLang = document.documentElement.lang;
  if (htmlLang && htmlLang.startsWith('ar')) {
    return 'ar';
  }

  // Check meta tag for language
  const metaLang = document.querySelector('meta[name="language"]');
  if (metaLang && metaLang.content && metaLang.content.startsWith('ar')) {
    return 'ar';
  }

  // Check URL path for Arabic locale
  const path = window.location.pathname;
  if (path.includes('/ar/') || path.startsWith('/ar')) {
    return 'ar';
  }

  // Check Shopify locale
  if (window.Shopify && window.Shopify.locale && window.Shopify.locale.startsWith('ar')) {
    return 'ar';
  }

  // Default to English
  return 'en';
}

// Translation mapping for Arabic and English
const TRANSLATIONS = {
  en: {
    'login_with_otp': 'Sign in',
    'enter_phone_number': 'Enter your phone number to receive a verification code',
    'phone_number': 'Phone Number',
    'request_otp': 'Send verification code',
    'or_login_using': 'Or',
    'facebook': 'Continue with Facebook',
    'google': 'Continue with Google',
    'login_with_email_password': 'Continue with Email',
    'enter_otp': 'Enter OTP',
    'otp_sent_on_mobile': 'The OTP is sent on Mobile Number',
    'change': 'Change',
    'verify_otp': 'Verify OTP',
    'resend_otp': 'Resend OTP',
    'didnt_receive_otp': 'Didn\'t receive the OTP',
    'enter_account_details': 'Enter Account Details',
    'first_name': 'First Name',
    'last_name': 'Last Name',
    'email_address': 'Email Address',
    'subscribe_newsletter': 'Subscribe to newsletter and promotional offers',
    'update': 'Update',
    'continue': 'Continue',
    'otp_sent_successfully': 'OTP sent successfully.',
    'otp_resent_successfully': 'OTP resent successfully.',
    'failed_to_resend_otp': 'Failed to resend OTP.',
    'please_enter_phone_number': 'Please enter a phone number.',
    'failed_to_send_otp': 'Failed to send OTP.',
    'please_enter_valid_otp': 'Please enter a valid 4-digit OTP.',
    'please_enter_email': 'Please enter your email.',
    'please_enter_email_continue': 'Please enter your email to continue.',
    'sms_verification_note': 'We\'ll send a one-time verification code via SMS'
  },
  ar: {
    'login_with_otp': 'تسجيل الدخول',
    'enter_phone_number': 'أدخل رقم هاتفك لتلقي رمز التحقق',
    'phone_number': 'رقم التليفون',
    'request_otp': 'إرسال رمز التحقق',
    'or_login_using': 'أو',
    'facebook': 'المتابعة باستخدام فيسبوك',
    'google': 'المتابعة باستخدام جوجل',
    'login_with_email_password': 'المتابعة بالبريد الإلكتروني',
    'enter_otp': 'أدخل رمز التحقق',
    'otp_sent_on_mobile': 'تم إرسال رمز التحقق على رقم الهاتف',
    'change': 'تغيير',
    'verify_otp': 'تأكيد رمز التحقق',
    'resend_otp': 'إعادة إرسال رمز التحقق',
    'didnt_receive_otp': 'لم أتلق رمز التحقق',
    'enter_account_details': 'أدخل تفاصيل الحساب',
    'first_name': 'الاسم الأول',
    'last_name': 'اسم العائلة',
    'email_address': 'عنوان البريد الإلكتروني',
    'subscribe_newsletter': 'الاشتراك في النشرة الإخبارية والعروض الترويجية',
    'update': 'تحديث',
    'continue': 'متابعة',
    'otp_sent_successfully': 'تم إرسال رمز التحقق بنجاح.',
    'otp_resent_successfully': 'تم إعادة إرسال رمز التحقق بنجاح.',
    'failed_to_resend_otp': 'فشل إعادة إرسال رمز التحقق.',
    'please_enter_phone_number': 'الرجاء إدخال رقم الهاتف.',
    'failed_to_send_otp': 'فشل إرسال رمز التحقق.',
    'please_enter_valid_otp': 'الرجاء إدخال رمز تحقق صالح مكون من 4 أرقام.',
    'please_enter_email': 'الرجاء إدخال بريدك الإلكتروني.',
    'please_enter_email_continue': 'الرجاء إدخال بريدك الإلكتروني للمتابعة.',
    'sms_verification_note': ' سنرسل لك رمز التحقق عبر رسالة نصية قصيرة.'
  }
};

// Function to get translated text
function getTranslation(key) {
  const language = detectWebsiteLanguage();
  return TRANSLATIONS[language] && TRANSLATIONS[language][key] ? TRANSLATIONS[language][key] : TRANSLATIONS.en[key];
}

// Function to apply language attribute to OTP container
function applyLanguageToContainer(container) {
  const language = detectWebsiteLanguage();
  if (language === 'ar') {
    container.setAttribute('lang', 'ar');
    container.setAttribute('dir', 'rtl');
  } else {
    container.setAttribute('lang', 'en');
    container.setAttribute('dir', 'ltr');
  }
}

// Function to get country display name from phone number
function getCountryDisplayName(phone) {
  if (!phone || typeof phone !== 'string') return '';

  if (phone.startsWith('+971')) return 'UAE';
  if (phone.startsWith('+973')) return 'BH';
  if (phone.startsWith('+965')) return 'KW';
  if (phone.startsWith('+968')) return 'OM';
  if (phone.startsWith('+966')) return 'SA';
  if (phone.startsWith('+974')) return 'QA';

  return '';
}

// Function to format phone number for display
function formatPhoneDisplay(phone) {
  if (!phone || typeof phone !== 'string') return '';

  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If it starts with +, format it nicely
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.substring(0, 4); // +971, +973, etc.
    const number = cleaned.substring(4);

    // Format based on country code
    if (countryCode === '+971') {
      // UAE: +971 50 123 4567
      if (number.length >= 9) {
        return `+971 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    } else if (countryCode === '+973') {
      // Bahrain: +973 12345678
      if (number.length >= 8) {
        return `+973 ${number}`;
      }
    } else if (countryCode === '+965') {
      // Kuwait: +965 55557640 (no space in middle)
      if (number.length >= 8) {
        return `+965 ${number}`;
      }
    } else if (countryCode === '+968') {
      // Oman: +968 12345678
      if (number.length >= 8) {
        return `+968 ${number}`;
      }
    } else if (countryCode === '+966') {
      // Saudi Arabia: +966 50 123 4567
      if (number.length >= 9) {
        return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    } else if (countryCode === '+974') {
      // Qatar: +974 12345678
      if (number.length >= 8) {
        return `+974 ${number}`;
      }
    }

    // Default formatting for other countries
    return cleaned;
  }

  return cleaned;
}

// Function to extract phone number without country code
function extractPhoneWithoutCountryCode(phone) {
  if (!phone || typeof phone !== 'string') return '';

  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If it starts with +, remove the country code
  if (cleaned.startsWith('+')) {
    return cleaned.substring(4); // Remove the country code (+971, +973, etc.)
  }

  return cleaned;
}

// Function to get country code from phone number
function getCountryCodeFromPhone(phone) {
  if (!phone || typeof phone !== 'string') return '+971'; // Default to UAE

  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If it starts with +, extract the country code
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.substring(0, 4); // +971, +973, etc.
    return countryCode;
  }

  return '+971'; // Default to UAE
}

// Function to generate country options based on countryCode setting
function generateCountryOptions() {
  const settings = window.hyperOTPSettings;
  const countryCode = settings?.countryCode || 'ALL';

  const allCountries = [
    { code: '+965', name: 'KW' },
    { code: '+971', name: 'UAE' },
    { code: '+973', name: 'BH' },
    { code: '+968', name: 'OM' },
    { code: '+966', name: 'SA' },
    { code: '+974', name: 'QA' }
  ];

  // If countryCode is 'ALL', return all countries
  if (countryCode === 'ALL') {
    return allCountries;
  }

  // If countryCode is a specific country, return only that country
  const countryMap = {
    'KW': { code: '+965', name: 'KW' },
    'UAE': { code: '+971', name: 'UAE' },
    'BH': { code: '+973', name: 'BH' },
    'OM': { code: '+968', name: 'OM' },
    'SA': { code: '+966', name: 'SA' },
    'QA': { code: '+974', name: 'QA' }
  };

  return countryMap[countryCode] ? [countryMap[countryCode]] : allCountries;
}

// Function to generate social login buttons HTML based on settings
function generateSocialButtonsHTML() {
  const { google, facebook } = getEnabledSocialLogins();
  if (!google && !facebook) return '';

  let html = '<div class="otp-social-row">';

  if (google) {
    html += `
      <!-- Server-side Google SSO Button -->
      <button type="button" class="otp-social-btn google-sso-btn">
        <img src='https://img.icons8.com/color/48/google-logo.png' alt='Google' />
      </button>
    `;
  }

  if (facebook) {
    html += `
      <!-- Server-side Facebook SSO Button -->
      <button type="button" class="otp-social-btn social-btn facebook-sso-btn">
        <img src='https://img.icons8.com/fluency/48/facebook-new.png' alt='Facebook' />
        <span style="font-size:0;">Facebook</span>
      </button>
    `;
  }

  html += '</div>';
  return html;
}

// Function to fetch settings from app proxy
async function fetchHyperOTPSettings() {
  try {
    // Check if settings are available from window object (from login-embed.liquid)
    // Only use metafield settings if they exist and are not empty
    if (window.hyperOTPUserData &&
      window.hyperOTPUserData.settings &&
      typeof window.hyperOTPUserData.settings === 'object' &&
      window.hyperOTPUserData.settings !== null &&
      Object.keys(window.hyperOTPUserData.settings).length > 0) {
      window.hyperOTPSettings = window.hyperOTPUserData.settings;
      console.log("✅ HyperOTP Settings loaded from metafield:", window.hyperOTPUserData.settings);
      return window.hyperOTPUserData.settings;
    }

    // Fallback to API call if window object is not available
    console.log("⚠️ Settings not found in window object, falling back to API call");
    const res = await fetch("/apps/zoketootp", {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step: "settings" })
    });
    const result = await res.json();
    if (result.success) {
      window.hyperOTPSettings = result.settings;
      console.log("✅ HyperOTP Settings loaded from API:", result.settings);
      return result.settings;
    } else {
      console.error("❌ Failed to load settings:", result.error);
      return null;
    }
  } catch (error) {
    console.error("❌ Failed to load settings:", error);
    return null;
  }
}

// Inject Google SSO script
(function injectGoogleSSOScript() {
  if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
    const script = document.createElement('script');
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
})();

/**
 * SECTION: Main Login UI Setup
 * Sets up the login interface and handles form visibility
 */
// Start fetching settings as soon as script loads
let hyperOTPSettingsPromise = fetchHyperOTPSettings();

// Function to detect Shopify login errors
function isShopifyLoginErrorPresent() {
  // Check for common Shopify error selectors
  const errorSelectors = [
    '.form-message', // Shopify Dawn theme
    '.errors',       // Older themes
    '.form__message', // Some other themes
  ];
  return errorSelectors.some(sel => {
    const el = document.querySelector(sel);
    return el && el.textContent && el.textContent.trim().length > 0;
  });
}

// Function to find the best container for OTP form injection
function findBestContainer() {
  // Priority order for finding the best container
  const selectors = [
    'main',
    '#MainContent',
    '.container',
    '.login-form',
    '.row',
    '#CustomerLoginForm',
    '.account-form',
    'body'
  ];

  for (const selector of selectors) {
    const element = document.querySelector(selector);
    if (element) {
      // If we found .login-form, try to get its parent container
      if (selector === '.login-form') {
        return element.parentElement?.parentElement || element.parentElement || element;
      }
      // If we found #CustomerLoginForm, try to get its parent container
      if (selector === '#CustomerLoginForm') {
        return element.parentElement || element;
      }
      return element;
    }
  }

  return document.body;
}

// Mount OTP container directly under #MainContent as first child when possible
function mountOtpUnderMain(otpContainer) {
  const mainEl = document.getElementById('MainContent') || document.querySelector('main#MainContent, .main-content#MainContent');
  if (!otpContainer || !mainEl) return false;
  if (mainEl.firstElementChild) {
    mainEl.insertBefore(otpContainer, mainEl.firstElementChild);
  } else {
    mainEl.appendChild(otpContainer);
  }
  return true;
}

// Precisely hide the theme login wrapper that creates extra space
function hideExactThemeLoginWrapper() {
  if (!document.getElementById('otp-exact-wrapper-style')) {
    const style = document.createElement('style');
    style.id = 'otp-exact-wrapper-style';
    style.innerHTML = `
      main#MainContent:has(#otp-login-container) > .page-width.page-width--tiny.page-content { display: none !important; }
    `;
    document.head.appendChild(style);
  }
  const exactWrapper = document.querySelector('main#MainContent > .page-width.page-width--tiny.page-content');
  if (exactWrapper) {
    exactWrapper.dataset._otpExactHidden = 'true';
    exactWrapper.style.setProperty('display', 'none', 'important');
  }
}

function unhideExactThemeLoginWrapper() {
  const exactWrapper = document.querySelector('main#MainContent > .page-width.page-width--tiny.page-content');
  if (exactWrapper && exactWrapper.dataset._otpExactHidden === 'true') {
    exactWrapper.style.display = '';
    delete exactWrapper.dataset._otpExactHidden;
  }
  const style = document.getElementById('otp-exact-wrapper-style');
  if (style) style.remove();
}

// Keep hiding the wrapper if the theme re-injects/changes DOM
function startHideWrapperObserver() {
  const root = document.querySelector('main#MainContent, #MainContent');
  if (!root) return;
  if (window._otpHideWrapperObserver) return;
  const hideAll = () => {
    root.querySelectorAll(':scope > .page-width.page-width--tiny.page-content').forEach(el => {
      el.dataset._otpExactHidden = 'true';
      el.style.setProperty('display', 'none', 'important');
    });
  };
  hideAll();
  window._otpHideWrapperObserver = new MutationObserver(() => hideAll());
  window._otpHideWrapperObserver.observe(root, { childList: true });
}

function stopHideWrapperObserver() {
  if (window._otpHideWrapperObserver) {
    try { window._otpHideWrapperObserver.disconnect(); } catch (_) { }
    window._otpHideWrapperObserver = null;
  }
}

// Fallback for when DOM is already loaded
if (document.readyState === 'loading') {
  console.log("📋 DOM still loading - waiting for DOMContentLoaded...");
} else {
  console.log("📋 DOM already loaded - executing initialization immediately...");
  // Execute the initialization logic immediately
  (function initializeOTP() {
    console.log("📅 DOMContentLoaded event fired - starting main initialization...");
    // Only run on account login page
    if (!isOnAccountLoginPage()) {
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      return;
    }

    // If Shopify login error is present, do not inject OTP UI
    if (isShopifyLoginErrorPresent()) {
      console.log("⚠️ Shopify login error detected - not injecting OTP UI");
      // Optionally, remove any temp CSS you injected
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      return;
    }
    // 1. Render the OTP form immediately with loading state
    if (!document.getElementById('otp-login-container')) {
      const otpContainer = document.createElement("div");
      otpContainer.id = "otp-login-container";
      otpContainer.className = "otp-fade-in";
      otpContainer.style.cssText = `
        background: transparent;
        max-width: 470px;
        margin: 40px auto;
        padding: 12px 24px 24px 24px;
        opacity: 0;
        transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
       
      `;
      applyLanguageToContainer(otpContainer);
      renderInitialForm(otpContainer, { loadingSocial: true });
      // Prefer mounting under #MainContent; fallback to best container
      if (!mountOtpUnderMain(otpContainer)) {
        const container = findBestContainer();
        container.appendChild(otpContainer);
      }
      setTimeout(() => { otpContainer.style.opacity = '1'; }, 10);
    }

    // Remove loader if present
    const loader = document.getElementById('otp-loader');
    if (loader) loader.remove();

    // When settings are ready, update the UI
    hyperOTPSettingsPromise.then(settings => {
      window.hyperOTPSettings = settings;
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      const otpContainer = document.getElementById('otp-login-container');
      if (!settings || settings.appEnabled === false) {
        // Remove the temp CSS so default login shows
        if (tempStyle) tempStyle.remove();
        if (otpContainer) otpContainer.remove();
        return;
      }
      if (settings.loginMethod === 'ONLY_EMAIL_PASSWORD') {
        if (tempStyle) tempStyle.remove();
        showShopifyDefaultLoginForm();
        return;
      }
      // Apply dynamic button colors based on settings
      applyDynamicButtonColors();
      // Now update the OTP UI with real settings
      if (otpContainer) {
        renderInitialForm(otpContainer, { loadingSocial: false });
        const { google, facebook } = getEnabledSocialLogins();
        const showOTPForm = shouldShowOTPForm();
        const showEmailPassword = shouldShowEmailPasswordLogin();
        const googleButton = otpContainer.querySelector('.google-sso-btn');
        const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
        const emailButton = otpContainer.querySelector('.email-btn');
        const divider = otpContainer.querySelector('.divider');
        const otpFormElement = otpContainer.querySelector('#otp-form');
        if (googleButton) {
          if (google) {
            googleButton.disabled = false;
            googleButton.style.opacity = '1';
          } else {
            googleButton.style.display = 'none';
          }
        }
        if (facebookButton) {
          if (facebook) {
            facebookButton.disabled = false;
            facebookButton.style.opacity = '1';
          } else {
            facebookButton.style.display = 'none';
          }
        }
        if (emailButton) {
          if (showEmailPassword) {
            emailButton.disabled = false;
            emailButton.style.opacity = '1';
          } else {
            emailButton.style.display = 'none';
          }
        }
        if (otpFormElement) {
          otpFormElement.style.display = showOTPForm ? 'block' : 'none';
        }
        if (divider && !google && !facebook && !showEmailPassword) {
          divider.style.display = 'none';
        }
        attachGoogleSSOHandlers();
        attachFacebookSSOHandlers();
      }
      injectHideLoginCSS();
      if (tempStyle) tempStyle.remove();
    });
  })();
}

document.addEventListener("DOMContentLoaded", () => {
  console.log("📅 DOMContentLoaded event fired - starting main initialization...");
  // Only run on account login page
  if (!isOnAccountLoginPage()) {
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    return;
  }

  // If Shopify login error is present, do not inject OTP UI
  if (isShopifyLoginErrorPresent()) {
    console.log("⚠️ Shopify login error detected - not injecting OTP UI");
    // Optionally, remove any temp CSS you injected
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    return;
  }
  // 1. Render the OTP form immediately with loading state
  if (!document.getElementById('otp-login-container')) {
    const otpContainer = document.createElement("div");
    otpContainer.id = "otp-login-container";
    otpContainer.className = "otp-fade-in";
    otpContainer.style.cssText = `
      background: transparent;
      max-width: 470px;
      margin: 40px auto;
      padding: 12px 24px 24px 24px;
      opacity: 0;
      transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
     
    `;
    applyLanguageToContainer(otpContainer);
    renderInitialForm(otpContainer, { loadingSocial: true });
    // Prefer mounting under #MainContent; fallback to best container
    if (!mountOtpUnderMain(otpContainer)) {
      const targetContainerInit = findBestContainer();
      targetContainerInit.appendChild(otpContainer);
    }
    hideExactThemeLoginWrapper();
    startHideWrapperObserver();
    setTimeout(() => { otpContainer.style.opacity = '1'; }, 10);
  }

  // Remove loader if present
  const loader = document.getElementById('otp-loader');
  if (loader) loader.remove();

  // When settings are ready, update the UI
  hyperOTPSettingsPromise.then(settings => {
    window.hyperOTPSettings = settings;
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    const otpContainer = document.getElementById('otp-login-container');
    if (!settings || settings.appEnabled === false) {
      // Remove the temp CSS so default login shows
      if (tempStyle) tempStyle.remove();
      if (otpContainer) otpContainer.remove();
      unhideExactThemeLoginWrapper();
      unhideExactThemeLoginWrapper();
      return;
    }
    if (settings.loginMethod === 'ONLY_EMAIL_PASSWORD') {
      if (tempStyle) tempStyle.remove();
      showShopifyDefaultLoginForm();
      return;
    }
    // Apply dynamic button colors based on settings
    applyDynamicButtonColors();
    // Now update the OTP UI with real settings
    if (otpContainer) {
      renderInitialForm(otpContainer, { loadingSocial: false });
      const { google, facebook } = getEnabledSocialLogins();
      const showOTPForm = shouldShowOTPForm();
      const showEmailPassword = shouldShowEmailPasswordLogin();
      const googleButton = otpContainer.querySelector('.google-sso-btn');
      const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
      const emailButton = otpContainer.querySelector('.email-btn');
      const divider = otpContainer.querySelector('.divider');
      const otpFormElement = otpContainer.querySelector('#otp-form');
      if (googleButton) {
        if (google) {
          googleButton.disabled = false;
          googleButton.style.opacity = '1';
        } else {
          googleButton.style.display = 'none';
        }
      }
      if (facebookButton) {
        if (facebook) {
          facebookButton.disabled = false;
          facebookButton.style.opacity = '1';
        } else {
          facebookButton.style.display = 'none';
        }
      }
      if (emailButton) {
        if (showEmailPassword) {
          emailButton.disabled = false;
          emailButton.style.opacity = '1';
        } else {
          emailButton.style.display = 'none';
        }
      }
      if (otpFormElement) {
        otpFormElement.style.display = showOTPForm ? 'block' : 'none';
      }
      if (divider && !google && !facebook && !showEmailPassword) {
        divider.style.display = 'none';
      }
      attachGoogleSSOHandlers();
      attachFacebookSSOHandlers();
    }
    injectHideLoginCSS();
    if (tempStyle) tempStyle.remove();
  });
});

/**
 * SECTION: OTP Form Handler
 * Manages the OTP form submission, verification, and user registration process
 * @param {HTMLFormElement} form - The OTP form element
 * @param {HTMLElement} otpContainer - The container element for the OTP UI
 */
function attachOtpFormHandler(form, otpContainer) {
  const stepInput = form.querySelector('input[name="step"]');
  const identifierInput = form.querySelector('input[name="identifier"]');
  const countryCodeSelect = form.querySelector('#country-code');
  const otpInputContainer = form.querySelector(".otp-input-container");
  const otpInput = form.querySelector('input[name="otp"]');
  const nameInputWrapper = form.querySelector("#name-input-wrapper");
  const emailInputWrapper = form.querySelector("#email-input-wrapper");
  const firstNameInput = form.querySelector('input[name="first_name"]');
  const lastNameInput = form.querySelector('input[name="last_name"]');
  const emailInput = form.querySelector('input[name="email"]');
  const messageBox = document.getElementById("otp-message");
  const submitButton = form.querySelector("button[type='submit']");

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    // Fetch settings if not already loaded
    if (!window.hyperOTPSettings) {
      await fetchHyperOTPSettings();
    }

    const step = stepInput ? stepInput.value : "send";
    let identifier = identifierInput ? identifierInput.value.trim() : "";
    let countryCode = countryCodeSelect ? countryCodeSelect.value : "";
    // Remove all spaces from identifier before any further processing (for phone only)
    if (identifier && !identifier.includes("@")) {
      identifier = identifier.replace(/\s+/g, "");
    }
    // Only concatenate if not email and not already has country code
    if (identifier && countryCode && !identifier.startsWith("+") && !identifier.includes("@")) {
      identifier = countryCode + identifier;
    }
    const otp = otpInput?.value?.trim();

    // Phone validation for GCC countries
    function isValidGCCPhone(phone) {
      if (typeof phone !== 'string') return false;

      // UAE: +971XXXXXXXXX (9 digits after +971)
      if (phone.startsWith('+971')) {
        const number = phone.slice(4);
        return /^\d{9}$/.test(number);
      }

      // Bahrain: +973XXXXXXXX (8 digits after +973)
      if (phone.startsWith('+973')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }

      // Kuwait: +965XXXXXXXX (8 digits after +965)
      if (phone.startsWith('+965')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }

      // Oman: +968XXXXXXXX (8 digits after +968)
      if (phone.startsWith('+968')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }

      // Saudi Arabia: +966XXXXXXXX (8 digits after +966)
      if (phone.startsWith('+966')) {
        const number = phone.slice(4);
        return /^\d{9}$/.test(number);
      }

      // Qatar: +974XXXXXXXX (8 digits after +974)
      if (phone.startsWith('+974')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }

      return true; // For other countries, don't validate here
    }

    if (!identifier) {
      if (messageBox) {
        messageBox.style.display = "block";
        messageBox.style.color = "red";
        messageBox.innerText = getTranslation('please_enter_phone_number');
      }
      return;
    }
    // GCC phone validation (only for phone, not email)
    if (!identifier.includes("@") && !isValidGCCPhone(identifier)) {
      if (messageBox) {
        const countryCode = identifier.startsWith('+') ? identifier.substring(0, 4) : '';
        let errorMessage = "Please enter a valid mobile number.";

        if (countryCode === "+971") {
          errorMessage = "Please enter a valid UAE mobile number (9 digits, digits only).";
        } else if (countryCode === "+973") {
          errorMessage = "Please enter a valid Bahrain mobile number (8 digits, digits only).";
        } else if (countryCode === "+965") {
          errorMessage = "Please enter a valid Kuwait mobile number (8 digits, digits only).";
        } else if (countryCode === "+968") {
          errorMessage = "Please enter a valid Oman mobile number (8 digits, digits only).";
        } else if (countryCode === "+966") {
          errorMessage = "Please enter a valid Saudi Arabia mobile number (9 digits, digits only).";
        } else if (countryCode === "+974") {
          errorMessage = "Please enter a valid Qatar mobile number (8 digits, digits only).";
        }

        messageBox.style.display = "block";
        messageBox.style.color = "red";
        messageBox.innerText = errorMessage;
      }
      return;
    }

    if (step === "send") {
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      const res = await fetch("/apps/zoketootp", {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: identifier, step: "send" })
      });
      const result = await res.json();
      // console.log("[OTP]", result.otp);
      // console.log("[SEND OTP RESPONSE]", result);
      submitButton.disabled = false;

      if (result.success) {
        if (messageBox) {
          messageBox.style.display = "block";
          messageBox.style.color = "green";
          messageBox.innerText = result.message || getTranslation('otp_sent_successfully');
        }
        // Get enabled social logins and check if OTP form should be shown
        const { google, facebook } = getEnabledSocialLogins();
        const showSocialDivider = google || facebook;
        const showOTPForm = shouldShowOTPForm();
        const showEmailPassword = shouldShowEmailPasswordLogin();

        if (!showOTPForm) {
          // If OTP form should not be shown, just show social buttons and email password if enabled
          otpContainer.innerHTML = `
            <style>
              // ... existing styles ...
            </style>
            <h2 class="otp-title" style="color: ${window.hyperOTPSettings?.loginHeaderColor || 'white'}; font-size: 22px;">${getTranslation('login_with_otp')}</h2>
            <div id="otp-message" style="margin-bottom: 1rem; color: green;"></div>
            ${generateSocialButtonsHTML()}
            ${showEmailPassword ? `<button type="button" class="otp-email-btn">${getTranslation('login_with_email_password')}</button>` : ''}
            <div style="font-size: 10px; text-align: right; padding-top: 8px;">Powered by Zoketo</div>
          `;
          return;
        }

        // Custom OTP UI for verify step - New Figma Design
        const showEmailPassword2 = shouldShowEmailPasswordLogin();
        otpContainer.innerHTML = `
            <style>
              /* New Figma-based OTP Verification Design */
              .otp-verify-header {
                display: flex;
                flex-direction: column;
                gap: 8px;
                margin-bottom: 0px;
                text-align: center;
              }
              
              .otp-verify-title {
                font-family: var(--font-hurme) !important;
                font-size: 24px;
                font-weight: 700;
                line-height: 40px;
                letter-spacing: -0.3px;
                color: #00563f; 
                margin: 0;
              }
              
              .otp-verify-subtitle {
                font-family: var(--font-hurme) !important;
                font-size: 22px;
                font-weight: 300;
                line-height: normal;
                color: #00563f;
                margin: 0;
              }
              
              .otp-phone-display {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 12px;
                margin-top: 12px;
                margin-bottom: 24px;
              }
              
              .otp-phone-number {
                font-family: var(--font-hurme) !important;
                font-size: 18px;
                font-weight: 500;
                color: #00563f;
              }
              
              .otp-change-link {
                font-family: var(--font-hurme) !important;
                font-size: 16px;
                font-weight: 400;
                color: #0066CC;
                text-decoration: underline;
                cursor: pointer;
                background: none;
                border: none;
                padding: 0;
              }
              
              .otp-change-link:hover {
                color: #0052A3;
              }
              
              .otp-input-container {
                margin-bottom: 0;
              }
              
              .otp-verify-input {
                width: 100%;
                height: 64px;
                padding: 20px;
                background: #FDFDFD;
                border: 0.66px solid #8A8A8A;
                border-radius: 8px;
                font-family: var(--font-hurme) !important;
                font-size: 32px !important;
                font-weight: 600;
                color: #00563f;
                text-align: center;
                letter-spacing: 1.5em;
                outline: none;
                box-shadow: none !important;
                margin-bottom: 10px;
              }
              
              .otp-verify-input::placeholder {
                letter-spacing: normal;
                font-size: 18px;
                color: #8A8A8A;
              }
              
              .otp-verify-input:focus {
                background: #fff;
                border: 0.66px solid #8A8A8A;
                box-shadow: none !important;
                outline: none !important;
              }
              
              .otp-verify-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
              width: 100%;
                background: #235541;
                color: #ffffff;
      border: none;
                border-radius: 0px 30px 0px 30px;
                height: 64px;
      font-size: 18px;
                font-family: var(--font-hurme) !important;
                font-weight: 600;
                text-transform: uppercase;
      cursor: pointer;
      transition: background 0.2s;
                letter-spacing: 0;
                line-height: 24px;
                margin-bottom: 24px;
              }
              
              .otp-verify-btn:hover {
                background: #1a4133;
              }
              
              .otp-verify-btn-arrow {
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              
              .otp-resend-section {
                text-align: center;
                margin-top: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
              }
              
              .otp-resend-text {
                font-family: var(--font-hurme) !important;
                font-size: 16px;
                font-weight: 400;
                color: #00563f;
                margin: 0;
              }
              
              .otp-resend-link {
                font-family: var(--font-hurme) !important;
                font-size: 16px;
                font-weight: 400;
                color: #0066CC;
                text-decoration: underline;
        cursor: pointer;
                background: none;
                border: none;
                padding: 0;
                margin: 0;
              }
              
              .otp-resend-link:hover {
                color: #0052A3;
              }
              
              .otp-spinner {
                display: inline-block;
                width: 22px;
                height: 22px;
                border: 3px solid #fff;
                border-radius: 50%;
                border-top: 3px solid #235541;
                animation: otp-spin 0.7s linear infinite;
                vertical-align: middle;
                margin-right: 8px;
              }
              
              @keyframes otp-spin {
                0% { transform: rotate(0deg);}
                100% { transform: rotate(360deg);}
              }
              
              /* Hidden fields for name and email */
              .hidden-input-row {
                display: none;
                margin-bottom: 16px;
              }
              
              /* Show email input when required */
              .hidden-input-row.show-email-input {
                display: block !important;
                margin-top: 16px;
                margin-bottom: 16px;
              }
              
              .hidden-input-row.show-email-input .hidden-input {
                background: #FDFDFD;
                border: 0.66px solid #8A8A8A;
                border-radius: 8px;
                font-size: 16px;
                font-weight: 400;
                color: #00563f;
                text-align: left;
                padding: 16px;
                height: 56px;
              }
              
              .hidden-input-row.show-email-input .hidden-input:focus {
                background: #fff;
                border: 0.66px solid #8A8A8A;
                outline: none;
              }
              
              .hidden-input {
                width: 100%;
                height: 45px;
                padding: 0 16px;
                border: 0.66px solid #8A8A8A;
                background: #FDFDFD;
                font-size: 16px;
                color: #303030;
                font-family: var(--font-hurme) !important;
                border-radius: 8px;
                outline: none;
                letter-spacing: normal;
                text-align: left;
              }
              
              .hidden-input:focus {
                background: #fff;
                border: 0.66px solid #8A8A8A;
              }
              
              /* Divider and Social Buttons for OTP Verification */
              .divider { 
                display: flex; 
                align-items: center; 
                gap: 16px;
              }
              
              .divider::before, 
              .divider::after { 
                content: ''; 
                flex: 1; 
                height: 1px;
                background: #DEDEDE;
              }
              
              .divider-text {
                font-family: var(--font-hurme) !important;
                font-size: 14px;
                font-weight: 400;
                line-height: 20px;
                color: #00563f;
                white-space: nowrap;
              }
              
              .social-buttons-container {
                display: flex;
                flex-direction: column;
                gap: 8px;
              }
              
              .social-row {
                display: contents !important;
                gap: 8px;
              }
              
              .social-btn {
                flex: 1;
                display: flex !important;
                align-items: center;
                justify-content: center;
                gap: 12px;
                background: #efefef !important;
                border: none !important;
                border-radius: 8px !important;
                height: 52px !important;
                cursor: pointer;
                transition: background 0.2s;
                padding: 6px 12px !important;
                // box-shadow: 0px -1px 0px 0px inset #b5b5b5, -1px 0px 0px 0px inset #e3e3e3, 1px 0px 0px 0px inset #e3e3e3, 0px 1px 0px 0px inset #e3e3e3 !important;
                box-sizing: border-box !important;
                font-family: var(--font-hurme) !important;
                font-size: 18px;
                font-weight: 400;
                color: #00563f;
                letter-spacing: -0.2px;
                line-height: 24px;
              }
              
              .social-btn:disabled {
                cursor: not-allowed;
                opacity: 0.6;
              }
              
              .social-btn:hover:not(:disabled) {
                background: #f5f5f5 !important;
              }
              
              .social-btn img {
                width: 30px;
                height: 30px;
                display: block;
              }
              
              .social-btn-text {
                font-family: var(--font-hurme) !important;
                font-size: 18px;
                font-weight: 400;
                color: #00563f;
                letter-spacing: -0.2px;
                line-height: 24px;
              }
              
              .email-btn {
                width: 100% !important;       
                background: #ffffff !important;
                border: none !important;
                border-radius: 8px !important;
                height: 52px !important;
                font-size: 18px !important;
                font-family: var(--font-hurme) !important;
                font-weight: 400 !important;
                cursor: pointer !important;
                color: #00563f !important;
                transition: background 0.2s !important;
                padding: 6px 12px !important;
                // box-shadow: 0px -1px 0px 0px inset #b5b5b5, -1px 0px 0px 0px inset #e3e3e3, 1px 0px 0px 0px inset #e3e3e3, 0px 1px 0px 0px inset #e3e3e3 !important;
                letter-spacing: -0.2px !important;
                line-height: 24px !important;
                text-align: center !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                text-decoration: underline;
              }
              
              .email-btn:hover:not(:disabled) { 
                background: #f5f5f5 !important;
              }
              
              .email-btn:disabled {
                cursor: not-allowed;
                opacity: 0.6;
              }
              
              /* Mobile responsive */
              @media (max-width: 768px) {
                .otp-verify-title {
                  font-size: 24px;
                  line-height: 36px;
                }
                
                .otp-verify-subtitle {
                  font-size: 18px;
                }
                
                .otp-verify-input {
                  height: 56px;
                  font-size: 28px !important;
                  letter-spacing: 1em;
                }
                
                .otp-verify-btn {
                  height: 56px;
                  font-size: 16px;
                }
                
                .social-btn,
                .email-btn {
                  font-size: 16px !important;
                }
              }
            </style>
            
            <div class="otp-verify-header">
            <h2 class="otp-verify-title">${getTranslation('enter_otp')}</h2>
              <p class="otp-verify-subtitle">${getTranslation('otp_sent_on_mobile')}</p>
              <div class="otp-phone-display">
                <span class="otp-phone-number">${formatPhoneDisplay(identifier)}</span>
                <button type="button" id="otp-change-number" class="otp-change-link">${getTranslation('change')}</button>
              </div>
            </div>
            
            <div id="otp-message" style="margin-bottom: 1rem; color: red; display: none;"></div>
            
            <form id="otp-form">
              <input type="hidden" name="step" value="verify" />
              <input type="hidden" name="identifier" value="${identifier}" />
              
              <div class="otp-input-container">
                <input 
                  name="otp" 
                  placeholder="" 
                  class="otp-verify-input"
                  maxlength="4"
                  inputmode="numeric"
                  pattern="\\d{4}"
                  autocomplete="off"
                />
              </div>
              
              <button type="submit" class="otp-verify-btn">
                <span>${getTranslation('verify_otp')}</span>
                <div class="otp-verify-btn-arrow">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
              </div>
              </button>
              
              <div id="name-input-wrapper" class="hidden-input-row">
                <input name="first_name" placeholder="${getTranslation('first_name')}" class="hidden-input" style="margin-bottom: 8px;" />
                <input name="last_name" placeholder="${getTranslation('last_name')}" class="hidden-input" />
              </div>
              
              <div id="email-input-wrapper" class="hidden-input-row">
                <input name="email" placeholder="${getTranslation('email_address')}" class="hidden-input" type="email" />
            </div>
          </form>
            
            <div class="otp-resend-section">
              <p class="otp-resend-text">${getTranslation('didnt_receive_otp')}</p>
              <button type="button" id="otp-resend-link" class="otp-resend-link">${getTranslation('resend_otp')}</button>
            </div>
            
            ${showSocialDivider || showEmailPassword2 ? `
              <div class="divider" style="margin-top: 24px; margin-bottom: 24px;">
                <span class="divider-text">${getTranslation('or_login_using')}</span>
              </div>
              
              <div class="social-buttons-container">
            ${showSocialDivider ? `
                  <div class="social-row">
                    ${getEnabledSocialLogins().google ? `
                      <button type="button" class="social-btn google-sso-btn">
                        <img src='https://img.icons8.com/color/48/google-logo.png' alt='Google' />
                        <span class="social-btn-text">${getTranslation('google')}</span>
                      </button>
                    ` : ''}
                    ${getEnabledSocialLogins().facebook ? `
                      <button type="button" class="social-btn facebook-sso-btn">
                        <img src='https://img.icons8.com/fluency/48/facebook-new.png' alt='Facebook' />
                        <span class="social-btn-text">${getTranslation('facebook')}</span>
                      </button>
                    ` : ''}
              </div>
            ` : ''}
                ${showEmailPassword2 ? `<button type="button" class="email-btn">${getTranslation('login_with_email_password')}</button>` : ''}
              </div>
            ` : ''}
            
            <div style="font-size: 10px; text-align: right; padding-top: 16px; color: #8A8A8A;">Powered by Zoketo</div>
        `;
        // Apply dynamic button colors
        applyDynamicButtonColors();
        // Re-attach main handler to new OTP form
        const newForm = otpContainer.querySelector('#otp-form');
        attachOtpFormHandler(newForm, otpContainer);
        // Attach social login and email button handlers
        attachGoogleSSOHandlers();
        attachFacebookSSOHandlers();
        attachEmailPasswordHandlers();
        // Change number link
        otpContainer.querySelector('#otp-change-number').addEventListener('click', (e) => {
          e.preventDefault();
          // Get the identifier (phone) from the hidden input
          const identifierValue = newForm.querySelector('input[name="identifier"]').value;
          // Extract phone number without country code and get the country code
          const phoneWithoutCountryCode = extractPhoneWithoutCountryCode(identifierValue);
          const countryCode = getCountryCodeFromPhone(identifierValue);

          // Re-render the Get OTP form with the phone pre-filled
          renderInitialForm(otpContainer, { loadingSocial: false, prefillPhone: phoneWithoutCountryCode, selectedCountryCode: countryCode });
          const { google, facebook } = getEnabledSocialLogins();
          const showOTPForm = shouldShowOTPForm();
          const showEmailPassword = shouldShowEmailPasswordLogin();
          const googleButton = otpContainer.querySelector('.google-sso-btn');
          const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
          const emailButton = otpContainer.querySelector('.email-btn');
          const divider = otpContainer.querySelector('.divider');
          const otpFormElement = otpContainer.querySelector('#otp-form');
          if (googleButton) {
            if (google) {
              googleButton.disabled = false;
              googleButton.style.opacity = '1';
            } else {
              googleButton.style.display = 'none';
            }
          }
          if (facebookButton) {
            if (facebook) {
              facebookButton.disabled = false;
              facebookButton.style.opacity = '1';
            } else {
              facebookButton.style.display = 'none';
            }
          }
          if (emailButton) {
            if (showEmailPassword) {
              emailButton.disabled = false;
              emailButton.style.opacity = '1';
            } else {
              emailButton.style.display = 'none';
            }
          }
          if (otpFormElement) {
            otpFormElement.style.display = showOTPForm ? 'block' : 'none';
          }
          if (divider && !google && !facebook && !showEmailPassword) {
            divider.style.display = 'none';
          }
          attachGoogleSSOHandlers();
          attachFacebookSSOHandlers();
        });
        // Resend OTP link
        otpContainer.querySelector('#otp-resend-link').addEventListener('click', async (e) => {
          e.preventDefault();
          const resendLink = otpContainer.querySelector('#otp-resend-link');
          if (resendLink) {
            resendLink.innerHTML = '<span class="otp-spinner" style="display:inline-block;vertical-align:middle;min-width:22px;min-height:22px;"></span>';
            resendLink.style.pointerEvents = 'none';
          }
          // Get the identifier (phone) from the hidden input
          const identifierValue = newForm.querySelector('input[name="identifier"]').value;
          // Directly call the backend to resend OTP
          const messageBox = otpContainer.querySelector('#otp-message');
          try {
            const res = await fetch('/apps/zoketootp', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: identifierValue, step: 'send' })
            });
            const result = await res.json();
            if (result.success) {
              if (messageBox) {
                messageBox.style.display = 'block';
                messageBox.style.color = 'green';
                // Server returns "OTP sent successfully" but we want to show "OTP resent successfully"
                // Check if the message contains "OTP sent" and replace it with our translation
                const serverMessage = result.message || '';
                if (serverMessage.toLowerCase().includes('otp sent')) {
                  messageBox.innerText = getTranslation('otp_resent_successfully');
                } else {
                  messageBox.innerText = result.message || getTranslation('otp_resent_successfully');
                }
              }
            } else {
              if (messageBox) {
                messageBox.style.display = 'block';
                messageBox.style.color = 'red';
                messageBox.innerText = result.error || getTranslation('failed_to_resend_otp');
              }
            }
          } catch (err) {
            if (messageBox) {
              messageBox.style.display = 'block';
              messageBox.style.color = 'red';
              messageBox.innerText = getTranslation('failed_to_resend_otp');
            }
          } finally {
            if (resendLink) {
              resendLink.innerHTML = getTranslation('resend_otp');
              resendLink.style.pointerEvents = '';
            }
          }
        });
        return;
      } else {
        if (messageBox) {
          messageBox.style.display = "block";
          messageBox.style.color = "red";
          messageBox.innerText = result.error || getTranslation('failed_to_send_otp');
        }
        submitButton.innerHTML = getTranslation('request_otp');
      }
    } else if (step === "verify" || step === "complete") {
      // Validate OTP input
      if (!otp || otp.length !== 4 || !/^[0-9]{4}$/.test(otp)) {
        if (messageBox) {
          messageBox.style.display = "block";
          messageBox.style.color = "red";
          messageBox.innerText = getTranslation('please_enter_valid_otp');
        }
        return;
      }
      // If step is complete, validate email
      if (step === "complete") {
        if (!emailInput || !emailInput.value.trim()) {
          if (messageBox) {
            messageBox.style.display = "block";
            messageBox.style.color = "red";
            messageBox.innerText = getTranslation('please_enter_email');
          }
          return;
        }
      }
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      // Prepare payload
      let payload = { phone: identifier, otp, step };
      if (step === "complete") payload.email = emailInput.value.trim();
      const res = await fetch("/apps/zoketootp", {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      // console.log("[OTP VERIFY/COMPLETE RESPONSE]", result);
      submitButton.disabled = false;

      // Handle requireEmail response from backend
      if (result.requireEmail) {
        // Hide OTP input, show email input
        if (emailInputWrapper) {
          emailInputWrapper.classList.add('show-email-input');
          emailInputWrapper.style.display = 'block';
          // Add required attribute only when email field is shown
          const emailInput = emailInputWrapper.querySelector('input[name="email"]');
          if (emailInput) {
            emailInput.setAttribute('required', 'required');
          }
          // Move email input field to appear before the button
          if (otpInputContainer && submitButton) {
            otpInputContainer.parentNode.insertBefore(emailInputWrapper, submitButton);
          }
        }
        // Hide OTP input container
        if (otpInputContainer) otpInputContainer.style.display = 'none';
        if (submitButton) submitButton.innerHTML = `<span>${getTranslation('continue')}</span>`;
        if (stepInput) stepInput.value = 'update_email';
        form.dataset.customerId = result.customerId;
        if (messageBox) {
          messageBox.style.display = 'block';
          messageBox.style.color = 'red';
          messageBox.innerText = result.message || getTranslation('please_enter_email_continue');
        }
        return;
      }

      // NEW: If new customer, show signup form (name/email)
      if (result.success && result.existingCustomer === false) {
        // Render a separate signup form for new customers
        otpContainer.style.background = 'transparent';
        otpContainer.style.maxWidth = '484px';
        otpContainer.style.margin = '40px auto';
        otpContainer.style.padding = '40px 32px 32px 32px';
        otpContainer.style.display = 'flex';
        otpContainer.style.flexDirection = 'column';
        otpContainer.style.gap = '0';
        // Check registerFields setting
        const registerFields = (window.hyperOTPSettings && window.hyperOTPSettings.registerFields) ? window.hyperOTPSettings.registerFields : '';
        // Check acceptEnabled setting
        const acceptEnabled = (window.hyperOTPSettings && window.hyperOTPSettings.acceptEnabled) ? window.hyperOTPSettings.acceptEnabled : false;
        otpContainer.innerHTML = `
          <style>
            /* New Figma-based Signup Form Design */
            .signup-header {
              display: flex;
              flex-direction: column;
              gap: 8px;
              margin-bottom: 36px;
              text-align: center;
            }
            
            .signup-title {
              font-family: var(--font-hurme) !important;
              font-size: 30px;
              font-weight: 700;
              line-height: 40px;
              letter-spacing: -0.3px;
              color: #00563f;
              margin: 0;
            }
            
            .signup-subtitle {
              font-family: var(--font-hurme) !important;
              font-size: 22px;
              font-weight: 300;
              line-height: normal;
              color: #00563f;
              margin: 0;
            }
            
            .signup-form-container {
              display: flex;
              flex-direction: column;
              gap: 10px;
              margin-bottom: 10px;
            }
            
            .signup-input {
              width: 100%;
              height: 64px;
              padding: 20px;
              background: #FDFDFD;
              border: 0.66px solid #8A8A8A;
              border-radius: 8px;
              font-family: var(--font-hurme) !important;
              font-size: 16px;
              font-weight: 400;
              color: #00563f;
              outline: none;
              box-shadow: none !important;
              transition: background 0.2s, border 0.2s;
            }
            
            .signup-input::placeholder {
              color: #00563f;
              opacity: 1;
              font-size: 16px;
            }
            
            .signup-input:focus {
              background: #fff;
              border: 0.66px solid #8A8A8A;
              box-shadow: none !important;
              outline: none !important;
            }
            
            .signup-input[readonly] {
              background: #F5F5F5;
              color: #00563f;
              cursor: not-allowed;
            }
            
            .signup-btn {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              width: 100%;
              background: #235541;
              color: #ffffff;
              border: none;
              border-radius: 0px 30px 0px 30px;
              height: 64px;
              font-size: 18px;
              font-family: var(--font-hurme) !important;
              font-weight: 600;
              text-transform: uppercase;
              cursor: pointer;
              transition: background 0.2s;
              letter-spacing: 0;
              line-height: 24px;
              margin-top: 10px;
            }
            
            .signup-btn:hover {
              background: #1a4133;
            }
            
            .signup-btn-arrow {
              width: 24px;
              height: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            .signup-checkbox-wrapper {
              display: flex;
              align-items: flex-start;
              gap: 4px;
              cursor: pointer;
              margin-top: 8px;
              margin-bottom: 8px;
            }
            
            .signup-checkbox {
              width: 18px;
              height: 18px;
              margin: 2px 0 0 0;
              accent-color: #00563f;
              cursor: pointer;
            }
            
            .signup-checkbox-label {
              font-family: var(--font-hurme) !important;
              font-size: 14px;
              font-weight: 400;
              color: #00563f;
              line-height: 1.43;
              text-align: left;
              cursor: pointer;
            }
            
            .otp-spinner {
              display: inline-block;
              width: 22px;
              height: 22px;
              border: 3px solid #fff;
              border-radius: 50%;
              border-top: 3px solid #235541;
              animation: otp-spin 0.7s linear infinite;
              vertical-align: middle;
              margin-right: 8px;
            }
            
            @keyframes otp-spin {
              0% { transform: rotate(0deg);}
              100% { transform: rotate(360deg);}
            }
            
            /* Mobile responsive */
            @media (max-width: 768px) {
              .signup-title {
                font-size: 26px;
                line-height: 36px;
              }
              
              .signup-subtitle {
                font-size: 18px;
              }
              
              .signup-input {
                height: 56px;
                font-size: 14px;
              }
              
              .signup-btn {
                height: 56px;
                font-size: 16px;
              }
              
              .signup-checkbox-label {
                font-size: 10px;
                line-height: 1.3;
                white-space: nowrap;
              }
            }
          </style>
          
          <div class="signup-header">
            <h2 class="signup-title">${getTranslation('enter_account_details')}</h2>
            <p class="signup-subtitle">${getTranslation('otp_sent_on_mobile')}</p>
          </div>
          
          <form id="signup-form" autocomplete="off">
            <div class="signup-form-container">
            ${registerFields === 'email' ? '' : `
              <input 
                name="first_name" 
                id="first_name" 
                placeholder="${getTranslation('first_name')}" 
                class="signup-input"
                required
              />
              <input 
                name="last_name" 
                id="last_name" 
                placeholder="${getTranslation('last_name')}" 
                class="signup-input"
                required
              />
              `}
              <input 
                name="email" 
                id="email" 
                placeholder="${getTranslation('email_address')}" 
                class="signup-input" 
                type="email" 
                required 
              />
            </div>
            
            ${acceptEnabled ? `
            <div class="signup-checkbox-wrapper">
                <input type="checkbox" name="subscription" id="subscription" class="signup-checkbox" />
              <label for="subscription" class="signup-checkbox-label">${getTranslation('subscribe_newsletter')}</label>
            </div>
            ` : ''}
            
            <input type="hidden" name="phone" id="phone" value="${identifier}" />
            
            <button type="submit" class="signup-btn">
              <span>${getTranslation('update')}</span>
              <div class="signup-btn-arrow">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </button>
          </form>
          
          <div style="font-size: 10px; text-align: right; padding-top: 16px; color: #8A8A8A;">Powered by Zoketo</div>
        `;
        // Apply dynamic button colors
        applyDynamicButtonColors();
        // Attach handler for the new signup form
        const signupForm = otpContainer.querySelector('#signup-form');
        signupForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          const firstName = signupForm.querySelector('input[name="first_name"]')?.value.trim() || '';
          const lastName = signupForm.querySelector('input[name="last_name"]')?.value.trim() || '';
          const email = signupForm.querySelector('input[name="email"]')?.value.trim() || '';
          const phone = signupForm.querySelector('input[name="phone"]').value.trim();
          const subscription = signupForm.querySelector('input[name="subscription"]')?.checked || false;
          const submitBtn = signupForm.querySelector('button[type="submit"]');
          // Only require email
          if (!email) {
            alert(getTranslation('please_enter_email'));
            return;
          }
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<span class="otp-spinner"></span>';
          const res = await fetch('/apps/zoketootp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              phone,
              email,
              first_name: registerFields !== 'email' ? firstName : '',
              last_name: registerFields !== 'email' ? lastName : '',
              subscription,
              step: 'complete'
            })
          });
          const result = await res.json();
          submitBtn.disabled = false;
          if (result.success && result.email && result.password) {
            // Submit login form automatically with CAPTCHA
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/account/login';
            const emailInput = document.createElement('input');
            emailInput.type = 'hidden';
            emailInput.name = 'customer[email]';
            emailInput.value = result.email;
            form.appendChild(emailInput);
            const passwordInput = document.createElement('input');
            passwordInput.type = 'hidden';
            passwordInput.name = 'customer[password]';
            passwordInput.value = result.password;
            form.appendChild(passwordInput);
            const formTypeInput = document.createElement('input');
            formTypeInput.type = 'hidden';
            formTypeInput.name = 'form_type';
            formTypeInput.value = 'customer_login';
            form.appendChild(formTypeInput);
            const urlParams = new URLSearchParams(window.location.search);
            const returnUrl = result.return_url || urlParams.get('return_url') || urlParams.get('return_to');
            const checkoutUrl = urlParams.get('checkout_url');

            if (returnUrl) {
              const returnInput = document.createElement('input');
              returnInput.type = 'hidden';
              returnInput.name = 'return_to';
              returnInput.value = returnUrl;
              form.appendChild(returnInput);
            }
            if (checkoutUrl) {
              const checkoutInput = document.createElement('input');
              checkoutInput.type = 'hidden';
              checkoutInput.name = 'checkout_url';
              checkoutInput.value = checkoutUrl;
              form.appendChild(checkoutInput);
            }
            document.body.appendChild(form);
            if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
              window.Shopify.captcha.protect(form, () => { form.submit(); });
            } else {
              form.submit();
            }
            return;
          }
          if (result.error) {
            alert(result.error);
          } else {
            alert(result.message || 'Signup complete!');
          }
        });
        return;
      }
      // Success: login as usual
      if (result.success && result.email && result.password) {
        // Submit login form automatically with CAPTCHA
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/account/login';
        const emailInputHidden = document.createElement('input');
        emailInputHidden.type = 'hidden';
        emailInputHidden.name = 'customer[email]';
        emailInputHidden.value = result.email;
        form.appendChild(emailInputHidden);
        const passwordInput = document.createElement('input');
        passwordInput.type = 'hidden';
        passwordInput.name = 'customer[password]';
        passwordInput.value = result.password;
        form.appendChild(passwordInput);
        const formTypeInput = document.createElement('input');
        formTypeInput.type = 'hidden';
        formTypeInput.name = 'form_type';
        formTypeInput.value = 'customer_login';
        form.appendChild(formTypeInput);
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = result.return_url || urlParams.get('return_url') || urlParams.get('return_to');
        const checkoutUrl = urlParams.get('checkout_url');

        if (returnUrl) {
          const returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_to';
          returnInput.value = returnUrl;
          form.appendChild(returnInput);
        }
        if (checkoutUrl) {
          const checkoutInput = document.createElement('input');
          checkoutInput.type = 'hidden';
          checkoutInput.name = 'checkout_url';
          checkoutInput.value = checkoutUrl;
          form.appendChild(checkoutInput);
        }
        document.body.appendChild(form);
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(form, () => { form.submit(); });
        } else {
          form.submit();
        }
        return;
      }
      if (result.error) {
        if (messageBox) {
          messageBox.style.display = 'block';
          messageBox.style.color = 'red';
          messageBox.innerText = result.error;
        }
      } else if (result.message) {
        if (messageBox) {
          messageBox.style.display = 'block';
          messageBox.style.color = 'green';
          messageBox.innerText = result.message;
        }
      }
      submitButton.innerHTML = step === 'complete' ? getTranslation('continue') : getTranslation('verify_otp');
      return;
    } else if (step === "update_email") {
      // On submit, if step is update_email, send correct payload
      if (!emailInput || !emailInput.value.trim()) {
        if (messageBox) {
          messageBox.style.display = "block";
          messageBox.style.color = "red";
          messageBox.innerText = getTranslation('please_enter_email');
        }
        return;
      }
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      let payload = {
        step: "update_email",
        customerId: form.dataset.customerId,
        email: emailInput.value.trim(),
        phone: identifier
      };
      const res2 = await fetch("/apps/zoketootp", {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result2 = await res2.json();
      submitButton.disabled = false;
      // Handle login as normal
      if (result2.success && result2.email && result2.password) {
        // Submit login form automatically with CAPTCHA
        const formEl = document.createElement('form');
        formEl.method = 'POST';
        formEl.action = '/account/login';
        const emailInputHidden = document.createElement('input');
        emailInputHidden.type = 'hidden';
        emailInputHidden.name = 'customer[email]';
        emailInputHidden.value = result2.email;
        formEl.appendChild(emailInputHidden);
        const passwordInput = document.createElement('input');
        passwordInput.type = 'hidden';
        passwordInput.name = 'customer[password]';
        passwordInput.value = result2.password;
        formEl.appendChild(passwordInput);
        const formTypeInput = document.createElement('input');
        formTypeInput.type = 'hidden';
        formTypeInput.name = 'form_type';
        formTypeInput.value = 'customer_login';
        formEl.appendChild(formTypeInput);
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = result2.return_url || urlParams.get('return_url') || urlParams.get('return_to');
        const checkoutUrl = urlParams.get('checkout_url');

        if (returnUrl) {
          const returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_to';
          returnInput.value = returnUrl;
          formEl.appendChild(returnInput);
        }
        if (checkoutUrl) {
          const checkoutInput = document.createElement('input');
          checkoutInput.type = 'hidden';
          checkoutInput.name = 'checkout_url';
          checkoutInput.value = checkoutUrl;
          formEl.appendChild(checkoutInput);
        }
        document.body.appendChild(formEl);
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(formEl, () => { formEl.submit(); });
        } else {
          formEl.submit();
        }
        return;
      }
      if (result2.error) {
        if (messageBox) {
          messageBox.style.display = 'block';
          messageBox.style.color = 'red';
          messageBox.innerText = result2.error;
        }
      } else if (result2.message) {
        if (messageBox) {
          messageBox.style.display = 'block';
          messageBox.style.color = 'green';
          messageBox.innerText = result2.message;
        }
      }
      submitButton.innerHTML = getTranslation('continue');
      return;
    }
  });

  // Add OTP input restrictions and auto-submit logic
  if (otpInput) {
    // Remove any existing event listeners to prevent duplicates
    otpInput.removeEventListener('input', otpInput._autoSubmitHandler);

    otpInput._autoSubmitHandler = function (e) {
      // Remove non-numeric characters
      let val = otpInput.value.replace(/[^0-9]/g, '');
      // Limit to 4 digits
      if (val.length > 4) val = val.slice(0, 4);
      otpInput.value = val;

      // Auto-submit when 4th digit is entered
      if (val.length === 4) {
        console.log("🚀 Auto-submitting OTP form after 4th digit");
        // Small delay to ensure the input value is properly set
        setTimeout(() => {
          const submitButton = form.querySelector('button[type="submit"]');
          if (submitButton && !submitButton.disabled) {
            submitButton.click();
          }
        }, 50);
      }
    };

    otpInput.addEventListener('input', otpInput._autoSubmitHandler);
  }
}




/**
 * SECTION: SSO Management
 * Handles Single Sign-On (SSO) functionality for Google and Facebook
 */

/**
 * Manages SSO popup state to prevent multiple popups
 */
function isSSOPopupOpened() {
  return window._ssoPopupOpened || sessionStorage.getItem('ssoPopupOpened') === 'true';
}

function setSSOPopupOpened(val) {
  window._ssoPopupOpened = val;
  sessionStorage.setItem('ssoPopupOpened', val ? 'true' : 'false');
}

// Reset SSO popup state on page load
setSSOPopupOpened(false);

/**
 * SECTION: Google SSO Integration
 * Handles Google Single Sign-On functionality
 */

/**
 * Opens the Google SSO popup window
 */
function openGoogleSSOPopup() {
  console.log("openGoogleSSOPopup called");
  if (isSSOPopupOpened()) {
    console.log("SSO popup already opened, skipping");
    return;
  }
  setSSOPopupOpened(true);

  var shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'yourshop.myshopify.com';
  var urlParams = new URLSearchParams(window.location.search);
  var returnUrl = urlParams.get('return_url') || urlParams.get('return_to') || '/account';

  var popupUrl = 'https://otp.zoketo.com/app/authgoogle?shop=' + encodeURIComponent(shop);
  if (returnUrl) {
    popupUrl += '&return_url=' + encodeURIComponent(returnUrl);
  }

  // --- WebView/MobiLoud: Use same-window redirect instead of popup ---
  if (isInWebView()) {
    console.log("[SSO] WebView detected — using redirect flow for Google SSO");
    popupUrl += '&mode=redirect';
    popupUrl += '&return_origin=' + encodeURIComponent(window.location.origin);
    window.location.href = popupUrl;
    return;
  }

  // --- Normal browser: Use existing popup flow (unchanged) ---
  // Always include return_origin so the callback can build the absolute store URL
  // even if the popup path is taken (no-opener fallback needs it).
  var popupUrlWithOrigin = popupUrl + '&return_origin=' + encodeURIComponent(window.location.origin);
  console.log("Opening SSO popup for shop:", shop, "with return_url:", returnUrl);
  var popup = window.open(popupUrlWithOrigin, 'googleSSO', 'width=500,height=600');

  // Guard: some WebViews block window.open() and return null — fall back to redirect mode
  if (!popup) {
    console.log("[SSO] Popup was blocked — falling back to redirect mode for Google SSO");
    setSSOPopupOpened(false);
    var redirectUrl = popupUrl + '&mode=redirect&return_origin=' + encodeURIComponent(window.location.origin);
    window.location.href = redirectUrl;
    return;
  }

  // Monitor popup close event
  var checkClosed = setInterval(function () {
    if (!popup || popup.closed) {
      console.log("Google SSO popup was closed");
      setSSOPopupOpened(false);
      clearInterval(checkClosed);
    }
  }, 1000);
}

/**
 * Attaches click handlers to Google SSO buttons
 * Handles both static and dynamically added buttons
 */
function attachGoogleSSOHandlers() {
  document.querySelectorAll('.google-sso-btn').forEach(btn => {
    btn.removeEventListener('click', btn._googleSSOHandler);
    btn._googleSSOHandler = function (event) {
      console.log("Google SSO button clicked");
      event.preventDefault();
      openGoogleSSOPopup();
      return false;
    };
    btn.addEventListener('click', btn._googleSSOHandler);
  });
}

// Initial attach for Google SSO
attachGoogleSSOHandlers();

// Watch for dynamic UI changes
const googleSSOObserver = new MutationObserver(() => {
  attachGoogleSSOHandlers();
});
googleSSOObserver.observe(document.body, { childList: true, subtree: true });

/**
 * SECTION: Facebook SSO Integration
 * Handles Facebook Single Sign-On functionality
 */

/**
 * Opens the Facebook SSO popup window
 */
function openFacebookSSOPopup() {
  console.log("openFacebookSSOPopup called");
  if (isSSOPopupOpened()) {
    console.log("SSO popup already opened, skipping");
    return;
  }
  setSSOPopupOpened(true);

  var shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'yourshop.myshopify.com';
  var urlParams = new URLSearchParams(window.location.search);
  var returnUrl = urlParams.get('return_url') || urlParams.get('return_to') || '/account';

  var popupUrl = 'https://otp.zoketo.com/app/authfacebook?shop=' + encodeURIComponent(shop);

  // --- WebView/MobiLoud: Use same-window redirect instead of popup ---
  if (isInWebView()) {
    console.log("[SSO] WebView detected — using redirect flow for Facebook SSO");
    popupUrl += '&mode=redirect';
    popupUrl += '&return_origin=' + encodeURIComponent(window.location.origin);
    if (returnUrl) {
      popupUrl += '&return_url=' + encodeURIComponent(returnUrl);
    }
    window.location.href = popupUrl;
    return;
  }

  // --- Normal browser: Use existing popup flow (unchanged) ---
  // Always include return_origin so the callback can build the absolute store URL
  // even if the popup path is taken (no-opener fallback needs it).
  var popupUrlWithOrigin = popupUrl;
  if (returnUrl) popupUrlWithOrigin += '&return_url=' + encodeURIComponent(returnUrl);
  popupUrlWithOrigin += '&return_origin=' + encodeURIComponent(window.location.origin);
  console.log("Opening Facebook SSO popup for shop:", shop);
  var popup = window.open(popupUrlWithOrigin, 'facebookSSO', 'width=500,height=600');

  // Guard: some WebViews block window.open() and return null — fall back to redirect mode
  if (!popup) {
    console.log("[SSO] Popup was blocked — falling back to redirect mode for Facebook SSO");
    setSSOPopupOpened(false);
    var redirectUrl = popupUrl + '&mode=redirect&return_origin=' + encodeURIComponent(window.location.origin);
    if (returnUrl) redirectUrl += '&return_url=' + encodeURIComponent(returnUrl);
    window.location.href = redirectUrl;
    return;
  }

  // Monitor popup close event
  var checkClosed = setInterval(function () {
    if (!popup || popup.closed) {
      console.log("Facebook SSO popup was closed");
      setSSOPopupOpened(false);
      clearInterval(checkClosed);
    }
  }, 1000);
}

/**
 * Attaches click handlers to Facebook SSO buttons
 * Handles both static and dynamically added buttons
 */
function attachFacebookSSOHandlers() {
  // Handle buttons with facebook-sso-btn class
  document.querySelectorAll('.facebook-sso-btn').forEach(btn => {
    // Remove any old onclick attributes
    btn.removeAttribute('onclick');
    btn.removeEventListener('click', btn._facebookSSOHandler);
    btn._facebookSSOHandler = function (event) {
      console.log("Facebook SSO button clicked");
      event.preventDefault();
      openFacebookSSOPopup();
      return false;
    };
    btn.addEventListener('click', btn._facebookSSOHandler);
  });

  // Also handle generic social buttons with Facebook images (fallback)
  document.querySelectorAll('.social-btn').forEach(btn => {
    if (btn.querySelector('img[alt="Facebook"]')) {
      // Remove any old onclick attributes
      btn.removeAttribute('onclick');
      btn.removeEventListener('click', btn._facebookSSOHandler);
      btn._facebookSSOHandler = function (event) {
        console.log("Facebook SSO button clicked");
        event.preventDefault();
        openFacebookSSOPopup();
        return false;
      };
      btn.addEventListener('click', btn._facebookSSOHandler);
    }
  });
}

// Initial attach for Facebook SSO
attachFacebookSSOHandlers();

// Watch for dynamic UI changes
const fbObserver = new MutationObserver(() => {
  attachFacebookSSOHandlers();
});
fbObserver.observe(document.body, { childList: true, subtree: true });

/**
 * SECTION: SSO Redirect Callback Handler (WebView / MobiLoud)
 * When the SSO server uses redirect mode (no popup), it redirects the user back
 * to the store's /account/login page with credentials in the URL query string.
 * This function detects those params and submits the Shopify login form via
 * Shopify.captcha.protect() to avoid the "Missing CAPTCHA token" error.
 *
 * Expected URL params from the SSO server:
 *   ?sso_email=user@example.com
 *   &sso_password=<generated-password>
 *   &sso_return_url=/account          (optional)
 *   &sso_provider=google|facebook     (optional, for logging)
 */
function handleSSORedirectCallback() {
  try {
    var urlParams = new URLSearchParams(window.location.search);
    var ssoEmail = urlParams.get('sso_email');
    var ssoPassword = urlParams.get('sso_password');

    // Nothing to do — not a redirect-back from SSO server
    if (!ssoEmail || !ssoPassword) return;

    var ssoProvider = urlParams.get('sso_provider') || 'sso';
    console.log('[SSO Redirect] Detected redirect-back from', ssoProvider, '— submitting login form with CAPTCHA protection');

    // Clean the SSO params from the URL immediately so a page refresh doesn't re-trigger
    try {
      var cleanParams = new URLSearchParams(window.location.search);
      cleanParams.delete('sso_email');
      cleanParams.delete('sso_password');
      cleanParams.delete('sso_provider');
      cleanParams.delete('sso_return_url');
      var newSearch = cleanParams.toString() ? '?' + cleanParams.toString() : '';
      var cleanUrl = window.location.pathname + newSearch + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    } catch (_) {}

    // Resolve return URL: prefer the param sent by SSO server, then fall back to
    // whatever was in the URL before SSO params were stripped.
    var returnUrl = urlParams.get('sso_return_url') ||
                    urlParams.get('return_url') ||
                    urlParams.get('return_to') ||
                    '/account';
    var checkoutUrl = urlParams.get('checkout_url');

    // Build the Shopify customer login form
    var form = document.createElement('form');
    form.method = 'POST';
    form.action = '/account/login';

    var emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'customer[email]';
    emailInput.value = ssoEmail;
    form.appendChild(emailInput);

    var passwordInput = document.createElement('input');
    passwordInput.type = 'hidden';
    passwordInput.name = 'customer[password]';
    passwordInput.value = ssoPassword;
    form.appendChild(passwordInput);

    var formTypeInput = document.createElement('input');
    formTypeInput.type = 'hidden';
    formTypeInput.name = 'form_type';
    formTypeInput.value = 'customer_login';
    form.appendChild(formTypeInput);

    if (returnUrl) {
      var returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_to';
      returnInput.value = returnUrl;
      form.appendChild(returnInput);
    }

    if (checkoutUrl) {
      var checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout_url';
      checkoutInput.value = checkoutUrl;
      form.appendChild(checkoutInput);
    }

    document.body.appendChild(form);

    // Use Shopify CAPTCHA protection — this is the critical step that was missing
    // in the WebView redirect flow, causing the "Missing CAPTCHA token" error.
    if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
      console.log('[SSO Redirect] Submitting via Shopify.captcha.protect()');
      window.Shopify.captcha.protect(form, function () { form.submit(); });
    } else {
      // Shopify CAPTCHA API not loaded yet — wait briefly then retry
      console.log('[SSO Redirect] CAPTCHA API not ready, retrying in 800ms...');
      setTimeout(function () {
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(form, function () { form.submit(); });
        } else {
          console.warn('[SSO Redirect] CAPTCHA API unavailable — submitting without protection');
          form.submit();
        }
      }, 800);
    }
  } catch (err) {
    console.error('[SSO Redirect] Error in handleSSORedirectCallback:', err);
  }
}

// Run immediately on script load (handles redirect-back before DOMContentLoaded)
handleSSORedirectCallback();

/**
 * SECTION: SSO Message Handler
 * Processes messages from SSO popups and handles login
 */
window.addEventListener("message", function (event) {
  if (event.data && event.data.type === "shopify-sso-login") {
    setSSOPopupOpened(false); // Reset flag after successful login

    // Create and submit login form
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/account/login';

    // Add login credentials
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'customer[email]';
    emailInput.value = event.data.email;
    form.appendChild(emailInput);

    const passwordInput = document.createElement('input');
    passwordInput.type = 'hidden';
    passwordInput.name = 'customer[password]';
    passwordInput.value = event.data.password;
    form.appendChild(passwordInput);

    // Add form type for Shopify CAPTCHA
    const formTypeInput = document.createElement('input');
    formTypeInput.type = 'hidden';
    formTypeInput.name = 'form_type';
    formTypeInput.value = 'customer_login';
    form.appendChild(formTypeInput);

    // Use return_url from backend if present, else from query param
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = event.data.return_url || urlParams.get('return_url') || urlParams.get('return_to');
    const checkoutUrl = urlParams.get('checkout_url');

    console.log("[SSO] Submitting login form with return_url:", returnUrl, "checkout_url:", checkoutUrl);

    if (returnUrl) {
      const returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_to';
      returnInput.value = returnUrl;
      form.appendChild(returnInput);
    }
    if (checkoutUrl) {
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout_url';
      checkoutInput.value = checkoutUrl;
      form.appendChild(checkoutInput);
    }

    // Submit form with CAPTCHA protection if available
    document.body.appendChild(form);
    if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
      window.Shopify.captcha.protect(form, () => { form.submit(); });
    } else {
      form.submit();
    }
  }
});

/**
 * Renders the initial login/signup form with optional loading state for social buttons
 * @param {HTMLElement} otpContainer - The container to render into
 * @param {Object} options - { loadingSocial: boolean, prefillPhone: string }
 */
function renderInitialForm(otpContainer, options = {}) {
  const { loadingSocial = false, prefillPhone = '', selectedCountryCode = '' } = options;
  otpContainer.innerHTML = `
    <style>
      /* New Figma-based Design Styles */
      .login-header {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 36px;
        text-align: center;
      }
      
      .otp-login-title {
        font-family: var(--font-hurme) !important;
        font-size: 24px;
        font-weight: 700;
        line-height: 40px;
        letter-spacing: -0.3px;
        color: #00563f;
        margin: 0;
      }
      
      .login-subtitle {
        font-family: var(--font-hurme) !important;
        font-size: 18px;
        font-weight: 300;
        line-height: normal;
        color: #00563f;
        margin: 0;
      }
      
      .form-container {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 36px;
      }
      
      .input-group {
        display: flex;
        align-items: center;
        gap: 0px;
        padding: 6px 6px 6px 8px;
        background: #F9f9f9;
        border: none;
        border-radius: 8px;
        height: 56px;
        box-sizing: border-box;
      }
      
      .country-code-wrapper {
        display: flex;
        align-items: center;
        gap: 0px;
        flex-shrink: 0;
        background: #f0f0f0;
        border-radius: 8px 0 0 8px;
        padding: 0 10px 0 14px;
        height: calc(100% + 12px);
        border: 0px solid #D9D9D9;
        margin-right: 10px;
        margin-top: -6px;
        margin-bottom: -6px;
        margin-left: -8px;
      }
      
      .input-group select {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        font-size: 16px;
        font-family: var(--font-hurme) !important;
        font-weight: 400;
        color: #00563f;
        margin: 0;
        padding: 0;
        cursor: pointer;
        line-height: 20px;
      }
      
      .dropdown-arrow {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      }
      
      .dropdown-arrow svg {
        width: 9.5px;
        height: 5.5px;
      }
      
      .input-group input {
        flex: 1;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        font-size: 16px;
        font-family: var(--font-hurme) !important;
        font-weight: 400;
        color: #00563f;
        margin: 0;
        padding: 0;
        line-height: 20px;
      }
      
      .input-group input::placeholder {
        color: #00563f;
        opacity: 1;
      }
      
      .otp-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        width: 100%;
        background: #235541;
        color: #ffffff;
        border: none;
        border-radius: 0px 30px 0px 30px;
        height: 64px;
        font-size: 18px;
        font-family: var(--font-hurme) !important;
        font-weight: 400;
        text-transform: none;
        cursor: pointer;
        transition: background 0.2s;
        letter-spacing: 0;
        line-height: 24px;
        margin-top: 10px;
      }
      
      .otp-btn:hover { 
        background: #1a4133;
      }
      
      .otp-btn-arrow {
        width: 24px;
        height: 24px;
        display: none;
        align-items: center;
        justify-content: center;
      }
      
      .otp-btn-arrow svg {
        width: 24px;
        height: 24px;
        stroke: white;
        stroke-width: 2;
      }
      
      .divider { 
        display: flex; 
        align-items: center; 
        gap: 16px;
        margin-bottom: 36px;
        border-top: none !important;
      }
      
      .divider::before, 
      .divider::after { 
        content: ''; 
        flex: 1; 
        height: 1px;
        background: #DEDEDE;
      }
      
      .divider-text {
        font-family: var(--font-hurme) !important;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: #00563f;
        white-space: nowrap;
      }
      
      .social-buttons-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      
      .social-row {
        display: contents !important;
        gap: 8px;
      }
      
      .social-btn {
        flex: 1;
        display: flex !important;
        align-items: center;
        justify-content: center;
        gap: 12px;
        background: #efefef !important;
        border: none !important;
        border-radius: 8px !important;
        height: 52px !important;
        cursor: pointer;
        transition: background 0.2s;
        padding: 6px 12px !important;
        // box-shadow: 0px -1px 0px 0px inset #b5b5b5, -1px 0px 0px 0px inset #e3e3e3, 1px 0px 0px 0px inset #e3e3e3, 0px 1px 0px 0px inset #e3e3e3 !important;
        box-sizing: border-box !important;
        font-family: var(--font-hurme) !important;
        font-size: 18px;
        font-weight: 400;
        color: #00563f;
        letter-spacing: -0.2px;
        line-height: 24px;
      }
      
      .social-btn:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
      
      .social-btn:hover:not(:disabled) {
        background: #f5f5f5 !important;
      }
      
      .social-btn img {
        width: 30px;
        height: 30px;
        display: block;
      }
    
      .otp-sms-note {
        font-family: var(--font-hurme) !important;
        font-size: 15px;
        font-weight: 400;
        color: #00563f;
        text-align: center;
        margin: 8px 0 0 0;
      }
      
      .social-btn-text {
        font-family: var(--font-hurme) !important;
        font-size: 18px;
        font-weight: 400;
        color: #00563f;
        letter-spacing: -0.2px;
        line-height: 24px;
      }
      
      .email-btn {
        width: 100% !important;       
        background: #ffffff !important;
        border: none !important;
        border-radius: 8px !important;
        height: 52px !important;
        font-size: 18px !important;
        font-family: var(--font-hurme) !important;
        font-weight: 400 !important;
        cursor: pointer !important;
        color: #00563f !important;
        transition: background 0.2s !important;
        padding: 6px 12px !important;
        // box-shadow: 0px -1px 0px 0px inset #b5b5b5, -1px 0px 0px 0px inset #e3e3e3, 1px 0px 0px 0px inset #e3e3e3, 0px 1px 0px 0px inset #e3e3e3 !important;
        letter-spacing: -0.2px !important;
        line-height: 24px !important;
        text-align: center !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        text-decoration: underline;
      }
      
      .email-btn:hover:not(:disabled) { 
        background: #f5f5f5 !important;
      }
      
      .email-btn:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
      
      .otp-spinner {
        display: inline-block;
        width: 22px;
        height: 22px;
        border: 3px solid #fff;
        border-radius: 50%;
        border-top: 3px solid #235541;
        animation: otp-spin 0.7s linear infinite;
        vertical-align: middle;
        margin-right: 8px;
      }
      
      @keyframes otp-spin {
        0% { transform: rotate(0deg);}
        100% { transform: rotate(360deg);}
      }
      
      /* Mobile responsive */
      @media (max-width: 768px) {
        .otp-login-title {
          font-size: 24px;
          line-height: 36px;
        }
        
        .login-subtitle {
          font-size: 18px;
        }
        
        .input-group {
          padding: 6px 6px 6px 8px;
          height: 52px;
        }
        
        .otp-btn {
          height: 56px;
          font-size: 16px;
          text-transform: none;
          font-weight: 400 !important;
        }
        
        .social-btn,
        .email-btn {
          font-size: 16px !important;
        }
      }
    </style>
    
    <div class="login-header">
      <h2 class="otp-login-title">${getTranslation('login_with_otp')}</h2>
      <p class="login-subtitle">${getTranslation('enter_phone_number')}</p>
    </div>
    
    <div id="otp-message" style="margin-bottom: 1rem; color: green; display: none;"></div>
    
    <div class="form-container">
      <form id="otp-form" autocomplete="off" style="display: contents;">
      <input type="hidden" name="step" value="send" />
      <div class="input-group">
          <div class="country-code-wrapper">
        <select id="country-code">
            ${generateCountryOptions().map(country =>
    `<option value="${country.code}" ${selectedCountryCode && country.code === selectedCountryCode ? 'selected' : ''}>${country.code}</option>`
  ).join('')}
        </select>
            <div class="dropdown-arrow">
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L5 5L9 1" stroke="#303030" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
      </div>
          </div>
            <input name="identifier" type="tel" inputmode="numeric" pattern="[0-9]*" placeholder="${getTranslation('phone_number')}" value="${prefillPhone ? prefillPhone : ''}" autocomplete="tel">
        </div>
        <button type="submit" class="otp-btn">
          <span>${getTranslation('request_otp')}</span>
          <div class="otp-btn-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </button>
        <p class="otp-sms-note">${getTranslation('sms_verification_note')}</p>
    </form>
    </div>
    
    ${(getEnabledSocialLogins().google || getEnabledSocialLogins().facebook || shouldShowEmailPasswordLogin()) ? `
    <div class="divider">
      <span class="divider-text">${getTranslation('or_login_using')}</span>
    </div>
    
    <div class="social-buttons-container">
    <div class="social-row">
      ${!loadingSocial ? `
        ${getEnabledSocialLogins().google ? `
          <button type="button" class="social-btn google-sso-btn">
            <img src='https://img.icons8.com/color/48/google-logo.png' alt='Google' />
              <span class="social-btn-text">${getTranslation('google')}</span>
          </button>
        ` : ''}
        ${getEnabledSocialLogins().facebook ? `
            <button type="button" class="social-btn facebook-sso-btn">
            <img src='https://img.icons8.com/fluency/48/facebook-new.png' alt='Facebook' />
              <span class="social-btn-text">${getTranslation('facebook')}</span>
          </button>
        ` : ''}
      ` : ''}
    </div>
      ${shouldShowEmailPasswordLogin() ? `<button type="button" class="email-btn" disabled>${getTranslation('login_with_email_password')}</button>` : ''}
    </div>
    ` : ''}
    
    <div style="font-size: 10px; text-align: right; padding-top: 16px; color: #8A8A8A;">Powered by Zoketo</div>
  `;

  // Attach OTP form handler immediately
  const otpForm = otpContainer.querySelector('#otp-form');
  if (otpForm) {
    attachOtpFormHandler(otpForm, otpContainer);
    // Remove whitespace as user types in phone input
    const phoneInput = otpForm.querySelector('input[name="identifier"]');
    if (phoneInput) {
      phoneInput.addEventListener('input', function (e) {
        const cursor = phoneInput.selectionStart;
        const newValue = phoneInput.value.replace(/\s+/g, '');
        if (phoneInput.value !== newValue) {
          phoneInput.value = newValue;
          phoneInput.setSelectionRange(cursor - 1, cursor - 1);
        }
      });
    }
  }
}

// Function to apply dynamic button colors based on settings
// NOTE: This function is kept for backward compatibility but does not override the new Figma design
function applyDynamicButtonColors() {
  // Removed dynamic button color override to maintain Figma design consistency
  // The new design uses fixed colors: #235541 for primary buttons
  return;
}

// Helper function to adjust color brightness
function adjustColorBrightness(hex, percent) {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse the hex color
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Adjust brightness
  const factor = 1 + (percent / 100);
  const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
  const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
  const newB = Math.min(255, Math.max(0, Math.round(b * factor)));

  // Convert back to hex
  return '#' +
    (newR < 16 ? '0' : '') + newR.toString(16) +
    (newG < 16 ? '0' : '') + newG.toString(16) +
    (newB < 16 ? '0' : '') + newB.toString(16);
}

// Function to inject CSS to hide Shopify login form, only if appEnabled is true
function injectHideLoginCSS() {
  if (!isOnAccountLoginPage()) return;
  if (document.getElementById('hyperotp-hide-login-style')) return;
  const style = document.createElement("style");
  style.id = "hyperotp-hide-login-style";
  style.innerHTML = `
    form[action="/account/login"],
    #customer_login,
    .customer.login,
    [data-login-form],
    .shopify-section--main-password,
    .shopify-section--main-login,
    .section-header__title,
    .login-title,
    .form-title,
    .customer-login-title,
    form[action="/account/login"] *,
    #customer_login h1,
    #customer_login h2,
    .customer.login h1,
    .customer.login h2,
    .login-form,
    .login-form *,
    .container .row .col-lg-6:has(.login-form),
    .container .row .col-lg-6 .login-form,
    .container .row .col-lg-6 h2,
    .container .row .col-lg-6 form,
    .container .row .col-lg-6 .form-group,
    .container .row .col-lg-6 .btn,
    #CustomerLoginForm,
    #CustomerLoginForm *,
    .account-form,
    .account-form *,
    h1.title.row {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      z-index: -1 !important;
    }
  `;
  document.head.appendChild(style);
}

// Function to show Shopify default login form and remove custom UI
function showShopifyDefaultLoginForm() {
  // Ensure theme login is visible first
  document.documentElement.classList.add('otp-ready');
  try { if (window.revealThemeLogin) window.revealThemeLogin(); } catch (_) { }

  // Remove custom OTP UI with fade-out
  const otpContainer = document.getElementById('otp-login-container');
  if (otpContainer) {
    otpContainer.classList.add('otp-fade-out');
    setTimeout(() => {
      otpContainer.remove();
      // Remove both temp and permanent hide-login CSS
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      const permStyle = document.getElementById('hyperotp-hide-login-style');
      if (permStyle) permStyle.remove();
      stopHideWrapperObserver();
      // Optionally scroll to login form
      const loginForm = document.querySelector('form[action="/account/login"]') ||
        document.querySelector('.login-form') ||
        document.querySelector('.container .row .col-lg-6');
      if (loginForm) loginForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
      unhideExactThemeLoginWrapper();
    }, 120); // match fade-out duration
  } else {
    // Remove both temp and permanent hide-login CSS
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    const permStyle = document.getElementById('hyperotp-hide-login-style');
    if (permStyle) permStyle.remove();
    stopHideWrapperObserver();
    // Optionally scroll to login form
    const loginForm = document.querySelector('form[action="/account/login"]') ||
      document.querySelector('.login-form') ||
      document.querySelector('.container .row .col-lg-6');
    if (loginForm) loginForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
    unhideExactThemeLoginWrapper();
  }
}

// Attach handler to all email/password login buttons
function attachEmailPasswordHandlers() {
  document.querySelectorAll('.otp-email-btn, .email-btn').forEach(btn => {
    btn.removeEventListener('click', btn._emailLoginHandler);
    btn._emailLoginHandler = function (event) {
      event.preventDefault();

      // 1. FIRST: Make theme login visible
      document.documentElement.classList.add('otp-ready');
      try { if (window.revealThemeLogin) window.revealThemeLogin(); } catch (_) { }
      ; (function () {
        // Remove any hide/delay styles and classes to avoid blank page
        document.documentElement.classList.remove('otp-delay');
        var ids = ['zoketo-otp-no-flash', 'otp-no-flash', 'otp-delay-login-style', 'hyperotp-hide-login-style', 'hyperotp-temp-hide-login-style'];
        ids.forEach(function (id) { var el = document.getElementById(id); if (el && el.parentNode) el.parentNode.removeChild(el); });
      })();
      try { restoreThemeLoginWrapper(); } catch (_) { }

      // 2. THEN: Remove OTP container after small delay to ensure theme login is visible
      setTimeout(() => {
        showShopifyDefaultLoginForm();
      }, 100); // Small delay to ensure theme login renders

      return false;
    };
    btn.addEventListener('click', btn._emailLoginHandler);
  });
}

// Initial attach for email/password login
attachEmailPasswordHandlers();

// Watch for dynamic UI changes
const emailBtnObserver = new MutationObserver(() => {
  attachEmailPasswordHandlers();
});
emailBtnObserver.observe(document.body, { childList: true, subtree: true });

// Add fade-out CSS for smooth transition
(function addOtpFadeOutCSS() {
  if (document.getElementById('otp-fade-out-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-fade-out-style';
  style.innerHTML = `
    #otp-login-container.otp-fade-out {
      opacity: 0;
      transition: opacity 0.12s ease;
    }
  `;
  document.head.appendChild(style);
})();

// Add fade-in CSS for OTP UI (in case of SSR or style removal)
(function addOtpFadeInCSS() {
  if (document.getElementById('otp-fade-in-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-fade-in-style';
  style.innerHTML = `
    .otp-fade-in {
      opacity: 0;
      transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
    }
    .otp-fade-in[style*="opacity: 1"] {
      opacity: 1 !important;
    }
  `;
  document.head.appendChild(style);
})();

// Add mobile margin/padding for OTP container
(function injectOtpMobileMarginCSS() {
  if (document.getElementById('otp-login-mobile-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-login-mobile-style';
  style.innerHTML = `
    #otp-login-container {
      position: relative !important;
      // min-height: 400px !important;
      padding-bottom: 12px !important;
    }
    @media (max-width: 600px) {
      #otp-login-container {
        margin: 26px 36px !important;
        padding: 12px 8px 8px 8px !important;
        max-width: 98vw !important;
        position: relative !important;
        overflow: hidden !important;
      }
      .otp-verify-form-row {
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden !important;
      }
      .otp-verify-input {
        font-size: 28px !important;
        letter-spacing: 0.4em !important;
        height: 50px !important;
        margin-top: 0 !important;
        max-width: 100% !important;
        width: 100% !important;f
        box-sizing: border-box !important;
      }
      .otp-verify-input::placeholder {
        font-size: 14px !important;
        letter-spacing: normal !important;
      }
      .email-input {
        font-size: 16px !important;
        letter-spacing: normal !important;
        text-align: left !important;
        padding: 0 12px !important;
      }
      .email-input::placeholder {
        font-size: 14px !important;
        letter-spacing: normal !important;
      }
    }
  `;
  document.head.appendChild(style);
})();